// src/components/ProfileClient.tsx

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Trash2, Pin, Shield,
  Share2, Repeat2, Link2, Heart, MessageSquare, ThumbsDown, ChevronDown,
  TrendingUp, TrendingDown, Flame, Calendar, Clock, MoreVertical, Edit3, Bell, Users
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../../utils/postUrls';
import AnimationLayer from './AnimationLayer';
import { getThemeAnimations } from '@theme/animation-config';
import type { ThemeAnimations } from '@theme/animation-config';
import {
  PixelatedTurtle
} from '@theme/design-constants';
import CoralFrameSvg from '@/assets/CoralFrame.svg';
import { ThemeCustomizerButton } from './ThemeCustomizer/index';
import ProfileThemeCustomizer from './ThemeCustomizer/ProfileThemeCustomizer';
import type { ThemeCustomization } from './ThemeCustomizer/ThemeCustomizerModal';
import { useTheme } from '../../context/ThemeContext';
import { useNavigation } from '../../context/NavigationContext';
import { transformColor, type ColorTransformOptions } from '../../utils/colorTransform';
import { getBackground, getCardBackground } from '../../utils/backgroundHelpers';
import { useScale, scaleW, scaleH } from '../../hooks/useScale';
import { useDynamicBackground } from '../../hooks/useDynamicBackground';
import { useSwipe } from '../../hooks/useSwipe';
import { getMediaUrl } from '../../hooks/useMediaUpload';
import { Post } from '../../types/post';
import { canModeratePost, getEditorType, getPinnerType, User as PermUser } from '../../utils/permissions';
import { selectPreviewComments } from '../../utils/commentPreview';
import { EditPostModal } from './EditPostModal';
import { PostBadges } from './PostBadges';
import { ProfilePostModal } from './PostCreation';
import LoadingScreen from './LoadingScreen';
import MainNavbar from './MainNavbar';
import { postEventBus } from '../../lib/postEventBus';
import { usePostSync } from '../../hooks/usePostSync';
import { commentEventBus } from '../../lib/commentEventBus';
import { useCommentSync } from '../../hooks/useCommentSync';
import { useNotificationSync } from '../../hooks/useNotificationSync';
import { cacheTheme } from '../../utils/themeCache';
import { normalizeLikeCounts } from '../shared/hooks/useFeedData';
import { cacheManager } from '../../utils/cacheManager';
import resolveAvatar from '../../lib/resolveAvatar';
import { SubPanelSwiper } from '../mobile/Navigation';
import { MobileProfileThemeCustomizer } from '../mobile/MobileThemeBottomSheet';

import { PRESET_THEMES } from '@theme/tokens';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
// =====================================================================
// MODULE-LEVEL THEME INITIALIZATION
// =====================================================================
// This runs IMMEDIATELY when the module loads (before React mounts)
// to prevent gray flash during hard reload (F5)
//
// Strategy: Extract userId from URL, check cache, and set sessionStorage
// BEFORE any React components initialize. This ensures LoadingScreen
// sees the correct theme from the very first render.
// =====================================================================

if (typeof window !== 'undefined' && window.location.pathname.startsWith('/profile/')) {
  try {
    // Extract username from URL (/profile/:username)
    const pathParts = window.location.pathname.split('/');
    const usernameFromUrl = pathParts[2];

    if (usernameFromUrl) {
      // Check if we have a cached theme for this profile
      // We need to iterate through cache keys to find matching username
      const keys = Object.keys(sessionStorage);
      const profileThemeKey = keys.find(key => key.startsWith('profileTheme_'));

      if (profileThemeKey) {
        const cached = sessionStorage.getItem(profileThemeKey);
        if (cached) {
          const parsed = JSON.parse(cached);

          // Extract userId from the cache key (format: profileTheme_{userId})
          const userId = profileThemeKey.replace('profileTheme_', '');

          // Immediately set currentPageTheme for LoadingScreen
          sessionStorage.setItem('currentPageTheme', JSON.stringify({
            type: 'profile',
            userId: userId,
            colors: {
              primaryColor: parsed.primaryColor,
              secondaryColor: parsed.secondaryColor || parsed.primaryColor,
              textColor: parsed.textColor || '#ffffff',
              accentColor: parsed.accentColor || parsed.primaryColor
            }
          }));

          console.log('🎨 [ProfileClient] Module-level theme initialized for LoadingScreen');
        }
      }
    }
  } catch (e) {
    console.warn('⚠️ [ProfileClient] Module-level theme init failed:', e);
  }
}

type User = {
  id: string;
  username: string;
  displayName?: string;
  title?: string;
  bio?: string;
  avatar?: string;
  isCurrentUser?: boolean;
  isFollowing?: boolean;
  isMutualFollowers?: boolean;
  profileCustomization?: Record<string, unknown>;
  posts: Array<{
    id: string;
    title?: string;
    slug?: string;
    content: string;
    mediaUrl?: string;
    aboveContent?: boolean;
    belowContent?: boolean;
    createdAt: Date;
    likes: number;
    comments: number;
    reposts?: number;
    liked?: boolean;
    pinned?: boolean;
  }>;
};

type Props = {
  user: User;
  currentUser?: User;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  onFollowChange?: () => void;
  mobileMode?: boolean;
  onSectionChange?: (index: number) => void;
  mobileScreenWidth?: number;
};

const DEFAULT_BIO = `"We don't desire to rule the Earth. Only to serve it."
"Together, we build virtual worlds that heal the real one."`;

// Use ocean-turtle preset as the default for profiles
const defaultProfileCustomization: ThemeCustomization = {
  ...PRESET_THEMES['ocean-turtle'],
  navbarOpacity: 92,
  tabBarOpacity: 88,
  cardGradientFavorability: 50,
  cardOpacity: 100,
  cardGradientStrength: 100,
  gradientEnabled: true,
  gradientAngle: 135,
  gradientFavorability: 50,
  gradientStrength: 100,
  bubbleColor: PRESET_THEMES['ocean-turtle'].accentColor,
  turtleColor: PRESET_THEMES['ocean-turtle'].cardGradientColor2 || '#00897b',
  coralColors: [
    PRESET_THEMES['ocean-turtle'].accentColor,
    PRESET_THEMES['ocean-turtle'].secondaryColor || '#00acc1',
    PRESET_THEMES['ocean-turtle'].cardGradientColor2 || '#00897b',
    PRESET_THEMES['ocean-turtle'].cardGradientColor1 || '#00695c',
    PRESET_THEMES['ocean-turtle'].primaryColor || '#006064'
  ],
  profileFrameColor: PRESET_THEMES['ocean-turtle'].accentColor,
  profileFrameShape: 'square',
  profileNameAlign: 'left',
  profileFrameDesign: 'coral',
  animationsEnabled: true,
  selectedCharacter: 'turtle',
  selectedDecoration: 'coral',
  focusAnimationEnabled: false,
  presetTheme: 'ocean-turtle',
  // Alignment and positioning defaults
  cardPosition: 'center',
  tabAlignment: 'left',
  profilePicturePosition: 'left',
  profilePictureSize: 'medium',
  profileTitleAlign: 'left',
  profileBioAlign: 'left',
  postsAlignment: 'center'
};

export default function ProfileClient({ user, currentUser, isAdmin = false, isSuperAdmin = false, onFollowChange, mobileMode = false, onSectionChange, mobileScreenWidth = 390 }: Props) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { setCurrentTheme } = useNavigation();

  // Debug: Log when component renders (disabled to reduce noise)

  const { logout, setShowLoginModal } = useAuth();
  const rawScale = useScale();
  const scale = mobileMode ? { w: 1, h: 1 } : rawScale;

  // Debug logging for moderation permissions
  const backgroundHeight = useDynamicBackground();
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [showModerationModal, setShowModerationModal] = useState(false);
  const [profilePic, setProfilePic] = useState(user.avatar || null);
  const [profileDecoration, setProfileDecoration] = useState<string | null>(null);
  const [headerDesign, setHeaderDesign] = useState<string | null>(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(user.isFollowing || false);
  const [isMutualFollowers, setIsMutualFollowers] = useState(user.isMutualFollowers || false);
  const [isContentUpdating, setIsContentUpdating] = useState(false);

  // Moderation modal state
  const [modProfileName, setModProfileName] = useState(user.displayName || user.username);
  const [modProfileTitle, setModProfileTitle] = useState(user.title || 'Earthling');
  const [modProfileBio, setModProfileBio] = useState(user.bio || DEFAULT_BIO);
  const [modUsername, setModUsername] = useState(user.username);

  // Post widget state
  const [isShareAnimating, setIsShareAnimating] = useState(false);
  const [showPostWidget, setShowPostWidget] = useState(false);

  // Focus animation state
  const [focusedPostId, setFocusedPostId] = useState<string | null>(null);
  const [focusedPostIndex, setFocusedPostIndex] = useState<number>(-1);
  const postRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const currentFocusIndexRef = useRef<number>(-1);
  const isThrottledRef = useRef<boolean>(false);

  // Repost/Share modal state
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostPostId, setRepostPostId] = useState<string | null>(null);
  const [repostComment, setRepostComment] = useState('');
  const [isReposting, setIsReposting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePostId, setSharePostId] = useState<string | null>(null);
  const [showModerateMenu, setShowModerateMenu] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<Post | null>(null);

  // Draggable modal state
  const [repostModalPosition, setRepostModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const repostModalRef = useRef<HTMLDivElement>(null);

  // Animation configuration state
  const [animations, setAnimations] = useState<ThemeAnimations>(() => {
    const saved = user.profileCustomization as any;
    const presetTheme = saved?.presetTheme || 'ocean-turtle';
    const initialAnimations = getThemeAnimations(presetTheme);
    return initialAnimations;
  });
  const [animationsEnabled, setAnimationsEnabled] = useState(true);

  // Debug: Log whenever animations state changes
  useEffect(() => {
  }, [animations, animationsEnabled]);

  // Core state
  const [isMobile, setIsMobile] = useState(false);
  const [profileName, setProfileName] = useState(user.displayName || user.username);
  const [profileTitle, setProfileTitle] = useState(user.title || 'Earthling');
  const [profileBio, setProfileBio] = useState(user.bio || DEFAULT_BIO);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [newAdminPost, setNewAdminPost] = useState("");

  // Sync layout/structure when user identity changes (for profile navigation)
  useEffect(() => {
    setProfilePic(user.avatar || null);
    setModProfileName(user.displayName || user.username);
    setModProfileTitle(user.title || 'Earthling');
    setModProfileBio(user.bio || DEFAULT_BIO);
    setModUsername(user.username);
    setProfileName(user.displayName || user.username);
    setProfileTitle(user.title || 'Earthling');
    setProfileBio(user.bio || DEFAULT_BIO);

    // Reset animations from user's profile customization
    const saved = user.profileCustomization as any;
    const presetTheme = saved?.presetTheme || 'ocean-turtle';
    const initialAnimations = getThemeAnimations(presetTheme);
    setAnimations(initialAnimations);

    // Reset posts when user changes - match original initialization structure
    setPosts(user.posts.map((p: any) => ({
      id: p.id,
      title: p.title || 'Post',
      slug: p.slug,
      content: p.content,
      mediaUrl: p.mediaUrl,
      aboveContent: p.aboveContent,
      belowContent: p.belowContent,
      pinned: p.pinned || false,
      pinnedToFeed: p.pinnedToFeed || false,
      pinnedAt: p.pinnedAt,
      pinnedToFeedAt: p.pinnedToFeedAt,
      pinnedBy: p.pinnedBy,
      pinnedByType: p.pinnedByType,
      edited: false,
      author: p.author,
      createdAt: p.createdAt,
      likes: Array.isArray(p.likes) ? p.likes.length : (typeof p.likes === 'number' ? p.likes : 0),
      dislikes: Array.isArray(p.dislikes) ? p.dislikes.length : (typeof p.dislikes === 'number' ? p.dislikes : 0),
      disliked: p.disliked || false,
      liked: p.liked || false,
      comments: p.comments,
      reposts: p.reposts || 0,
      replies: p.replies || [],
      shares: p.shares || 0,
      views: p.views || 0,
      communityId: p.communityId,
      communityName: p.communityName,
      communitySlug: p.communitySlug,
      tags: p.tags || [],
    })));
  }, [user.id, user.username, user.avatar, user.displayName, user.title, user.bio, user.profileCustomization, user.posts]);

  // Sync content updates separately with soft reload (no layout invalidation)
  useEffect(() => {
    // Show soft loading indicator
    setIsContentUpdating(true);

    // Small delay to make the transition visible
    const timer = setTimeout(() => {
      setIsFollowing(user.isFollowing || false);
      setIsMutualFollowers(user.isMutualFollowers || false);

      // Hide loading indicator after update
      setTimeout(() => setIsContentUpdating(false), 150);
    }, 100);

    return () => clearTimeout(timer);
  }, [user.isFollowing, user.isMutualFollowers]);

  // Profile-specific customization (non-color settings that are unique to profiles)
  const [profileCustomization, setProfileCustomization] = useState({
    profileFrameColor: theme.accentColor || '#14B8A6',
    profileFrameShape: 'square' as 'square' | 'circle',
    profileNameAlign: 'left' as 'left' | 'center' | 'right',
    profileTitleAlign: 'left' as 'left' | 'center' | 'right',
    profileBioAlign: 'left' as 'left' | 'center' | 'right',
    profileFrameDesign: 'coral' as 'coral' | 'waves' | 'bubbles',
    cardPosition: 'left' as 'left' | 'center' | 'right',
    tabAlignment: 'left' as 'left' | 'center' | 'right',
    profilePicturePosition: 'left' as 'left' | 'right',
    profilePictureSize: 'small' as 'small' | 'medium' | 'large',
    postsAlignment: 'center' as 'left' | 'center' | 'right', // Default to center to avoid conflicts
  });

  // Section navigation state - map cardPosition to section ('center' -> 'middle')
  const mapCardPositionToSection = (position: 'left' | 'center' | 'right'): 'left' | 'middle' | 'right' => {
    return position === 'center' ? 'middle' : position;
  };
  const [currentSection, setCurrentSection] = useState<'left' | 'middle' | 'right'>(
    mapCardPositionToSection(profileCustomization.cardPosition)
  );

  // Report section changes to mobile shell
  useEffect(() => {
    if (mobileMode && onSectionChange) {
      const sectionIndex = { left: 0, middle: 1, right: 2 }[currentSection];
      onSectionChange(sectionIndex);
    }
  }, [currentSection, mobileMode, onSectionChange]);

  // Swipe handlers
  const swipeRef = useSwipe({
    onSwipeLeft: () => {
      if (currentSection === 'middle') setCurrentSection('right');
      else if (currentSection === 'left') setCurrentSection('middle');
    },
    onSwipeRight: () => {
      if (currentSection === 'middle') setCurrentSection('left');
      else if (currentSection === 'right') setCurrentSection('middle');
    }
  });

  // Full customization state for the theme customizer modal
  // Use default ocean-turtle theme for uncustomized profiles
  // This ensures profile theme always overrides site theme
  const [customization, setCustomizationRaw] = useState<ThemeCustomization>(defaultProfileCustomization);

  // Wrap setCustomization with logging to debug state updates
  const setCustomization = (newValue: ThemeCustomization | ((prev: ThemeCustomization) => ThemeCustomization)) => {
    if (typeof newValue === 'function') {
      setCustomizationRaw((prev) => {
        const updated = newValue(prev);
        return updated;
      });
    } else {
      setCustomizationRaw(newValue);
    }
  };
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);

  // Create local theme with transformed colors for inline styles
  // This ensures inline styles like style={{ color: localTheme.textColor }} use transformed colors
  const localTheme = useMemo(() => {
    const colorSpace = customization.colorSpace || 'Off';

    // Helper to convert hex to rgba with opacity
    const hexToRgbaWithOpacity = (hex: string, opacity: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
    };

    // If color space is "Off", return raw colors without transformation
    if (colorSpace === 'Off') {
      // Simple: colors are stored as 7-char hex, opacity stored separately
      const navbarBg = customization.navbarBg
        ? hexToRgbaWithOpacity(customization.navbarBg, customization.navbarOpacity ?? 92)
        : hexToRgbaWithOpacity(customization.primaryColor, customization.navbarOpacity ?? 92);

      const tabBarBg = customization.tabBarBg
        ? hexToRgbaWithOpacity(customization.tabBarBg, customization.tabBarOpacity ?? 88)
        : navbarBg;

      const dropdownColor = customization.dropdownColor || customization.tabBarBg || customization.primaryColor;

      return {
        ...theme,
        textColor: customization.textColor,
        accentColor: customization.accentColor,
        primaryColor: customization.primaryColor,
        secondaryColor: customization.secondaryColor,
        cardBg: customization.cardBg,
        profileFrameColor: customization.profileFrameColor || customization.accentColor,
        cardGradientColor1: customization.cardGradientColor1,
        cardGradientColor2: customization.cardGradientColor2,
        navbarBg,
        tabBarBg,
        dropdownColor,
        highlightColor: customization.highlightColor || customization.accentColor,
        bubbleColor: customization.bubbleColor || customization.accentColor,
        turtleColor: customization.turtleColor || customization.accentColor,
      };
    }

    // Otherwise, apply color transformations
    const transformOptions: ColorTransformOptions = {
      colorSpace,
      temperatureLimit: customization.temperatureLimit ?? 50,
      magentaLimit: customization.magentaLimit ?? 50,
      intensityLimit: customization.intensityLimit ?? 50,
      saturationLimit: customization.backgroundSaturationLimit ?? 50,
      brightnessLimit: customization.backgroundBrightnessLimit ?? 50,
      redLimit: customization.redLimit ?? 50,
      greenLimit: customization.greenLimit ?? 50,
      blueLimit: customization.blueLimit ?? 50,
      contrast: customization.backgroundContrast ?? 50,
      hueGravity: customization.backgroundHueGravity ?? 0, // Default 0 = no gravity
      graySaturation: customization.backgroundGraySaturation ?? 0,
      targetHue: customization.backgroundDefaultHue ?? 184, // Default teal hue
    };

    // Transform ALL color properties from customization
    const transformedTextColor = transformColor(customization.textColor, transformOptions);
    const transformedAccentColor = transformColor(customization.accentColor, transformOptions);
    const transformedPrimaryColor = transformColor(customization.primaryColor, transformOptions);
    const transformedSecondaryColor = transformColor(customization.secondaryColor, transformOptions);
    const transformedCardBg = transformColor(customization.cardBg, transformOptions);
    const transformedProfileFrameColor = customization.profileFrameColor ? transformColor(customization.profileFrameColor, transformOptions) : transformedAccentColor;
    const transformedCardGradientColor1 = customization.cardGradientColor1 ? transformColor(customization.cardGradientColor1, transformOptions) : undefined;
    const transformedCardGradientColor2 = customization.cardGradientColor2 ? transformColor(customization.cardGradientColor2, transformOptions) : undefined;

    // Transform navbar and tab bar colors (stored as 7-char hex), then apply opacity
    const transformedNavbarBgColor = customization.navbarBg
      ? transformColor(customization.navbarBg, transformOptions)
      : transformedPrimaryColor;
    const navbarBg = hexToRgbaWithOpacity(transformedNavbarBgColor, customization.navbarOpacity ?? 92);

    const transformedTabBarBgColor = customization.tabBarBg
      ? transformColor(customization.tabBarBg, transformOptions)
      : transformedPrimaryColor;
    const tabBarBg = customization.tabBarBg
      ? hexToRgbaWithOpacity(transformedTabBarBgColor, customization.tabBarOpacity ?? 88)
      : navbarBg;

    const transformedDropdownColor = customization.dropdownColor ? transformColor(customization.dropdownColor, transformOptions) : transformedTabBarBgColor;
    const dropdownColor = transformedDropdownColor;

    const transformedHighlightColor = customization.highlightColor ? transformColor(customization.highlightColor, transformOptions) : transformedAccentColor;
    const transformedBubbleColor = customization.bubbleColor ? transformColor(customization.bubbleColor, transformOptions) : transformedAccentColor;
    const transformedTurtleColor = customization.turtleColor ? transformColor(customization.turtleColor, transformOptions) : transformedAccentColor;

    return {
      ...theme,
      textColor: transformedTextColor,
      accentColor: transformedAccentColor,
      primaryColor: transformedPrimaryColor,
      secondaryColor: transformedSecondaryColor,
      cardBg: transformedCardBg,
      profileFrameColor: transformedProfileFrameColor,
      cardGradientColor1: transformedCardGradientColor1,
      cardGradientColor2: transformedCardGradientColor2,
      navbarBg,
      tabBarBg,
      dropdownColor,
      highlightColor: transformedHighlightColor,
      bubbleColor: transformedBubbleColor,
      turtleColor: transformedTurtleColor,
    };
  }, [customization, theme]);

  // Cache the transformed profile theme to sessionStorage for loading screen
  useEffect(() => {
    if (!user.id || !isThemeLoaded) return;

    // Cache the transformed theme for this profile
    // CRITICAL: Include ALL theme properties so ThemeContext can use complete theme on page refresh
    cacheTheme({
      textColor: localTheme.textColor,
      accentColor: localTheme.accentColor,
      cardBg: localTheme.cardBg,
      appBg: getBackground(localTheme, customization, theme.appBg),
      navbarBg: localTheme.navbarBg,
      tabBarBg: localTheme.tabBarBg,
      primaryColor: localTheme.primaryColor,
      secondaryColor: localTheme.secondaryColor,
      colorSpace: customization.colorSpace || 'Off',
      // Include card gradient settings
      cardGradientColor1: localTheme.cardGradientColor1,
      cardGradientColor2: localTheme.cardGradientColor2,
      // Include opacity settings
      navbarOpacity: (localTheme as any).navbarOpacity,
      tabBarOpacity: (localTheme as any).tabBarOpacity,
      cardOpacity: (localTheme as any).cardOpacity,
      // Include navbar text color
      navbarTextColor: (localTheme as any).navbarTextColor,
      // CRITICAL: Include ALL background gradient settings for LoadingScreen continuity
      gradientEnabled: customization.gradientEnabled ?? true,
      gradientAngle: customization.gradientAngle ?? 135,
      gradientFavorability: customization.gradientFavorability ?? 50,
      gradientStrength: customization.gradientStrength ?? 100,
      // CRITICAL: Include animations to prevent flash of wrong animations on navigation
      animations: animations,
    }, 'profile', user.id);
  }, [localTheme, customization, user.id, isThemeLoaded, theme.appBg, animations]);

  // Live preview: dispatch profile theme update event when customization changes in modal
  // Use refs to avoid stale closure values in debounced callback
  const customizationRef = React.useRef(customization);
  const animationsRef = React.useRef(animations);
  const animationsEnabledRef = React.useRef(animationsEnabled);

  React.useEffect(() => {
    customizationRef.current = customization;
  }, [customization]);

  React.useEffect(() => {
    animationsRef.current = animations;
  }, [animations]);

  React.useEffect(() => {
    animationsEnabledRef.current = animationsEnabled;
  }, [animationsEnabled]);

  // Dispatch live preview event when theme changes
  useEffect(() => {
    if (showCustomizer) {
      const timeoutId = setTimeout(() => {
        const latestCustomization = customizationRef.current;
        const latestAnimations = animationsRef.current;
        const latestAnimationsEnabled = animationsEnabledRef.current;

        window.dispatchEvent(new CustomEvent('profileThemeUpdated', {
          detail: {
            customization: latestCustomization,
            animations: latestAnimations,
            animationsEnabled: latestAnimationsEnabled
          }
        }));
      }, 50); // 50ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [customization, animations, animationsEnabled, showCustomizer]);

  // Load user's saved profileCustomization on mount with multi-tier caching
  useEffect(() => {
    setIsThemeLoaded(false);

    // STEP 1: Try cache first (hot → warm tiers)
    if (!user.isCurrentUser) {
      const cached = cacheManager.get('profile', user.id);
      if (cached) {
        
        // Apply cached theme immediately
        setCustomization(prev => ({
          ...prev,
          ...cached.settings,
          // Use cached colors (already transformed if colorSpace !== 'Off')
          primaryColor: cached.colors.primaryColor,
          secondaryColor: cached.colors.secondaryColor,
          textColor: cached.colors.textColor,
          accentColor: cached.colors.accentColor,
          navbarBg: cached.colors.navbarBg,
          tabBarBg: cached.colors.tabBarBg,
          cardBg: cached.colors.cardBg,
          appBg: cached.colors.appBg
        }));

        // Load animations
        const presetTheme = cached.settings?.presetTheme || 'ocean-turtle';
        setAnimations(getThemeAnimations(presetTheme));
        if (cached.settings?.animationsEnabled !== undefined) {
          setAnimationsEnabled(cached.settings.animationsEnabled);
        }

        setIsThemeLoaded(true);
        return; // Done - cache hit!
      }
    }

    // STEP 2: Cache miss or current user - load from user.profileCustomization
    if (user.profileCustomization) {
      const saved = user.profileCustomization as any;

      
      // Load profile-specific customization
      if (saved.profileFrameColor) setProfileCustomization(prev => ({ ...prev, profileFrameColor: saved.profileFrameColor }));
      if (saved.profileFrameShape) setProfileCustomization(prev => ({ ...prev, profileFrameShape: saved.profileFrameShape }));
      if (saved.profileNameAlign) setProfileCustomization(prev => ({ ...prev, profileNameAlign: saved.profileNameAlign }));
      if (saved.profileTitleAlign) setProfileCustomization(prev => ({ ...prev, profileTitleAlign: saved.profileTitleAlign }));
      if (saved.profileBioAlign) setProfileCustomization(prev => ({ ...prev, profileBioAlign: saved.profileBioAlign }));
      if (saved.profileFrameDesign) setProfileCustomization(prev => ({ ...prev, profileFrameDesign: saved.profileFrameDesign }));
      if (saved.cardPosition) setProfileCustomization(prev => ({ ...prev, cardPosition: saved.cardPosition }));
      if (saved.tabAlignment) setProfileCustomization(prev => ({ ...prev, tabAlignment: saved.tabAlignment }));
      if (saved.profilePicturePosition) setProfileCustomization(prev => ({ ...prev, profilePicturePosition: saved.profilePicturePosition }));
      if (saved.profilePictureSize) setProfileCustomization(prev => ({ ...prev, profilePictureSize: saved.profilePictureSize }));
      if (saved.postsAlignment) setProfileCustomization(prev => ({ ...prev, postsAlignment: saved.postsAlignment }));

      // Load theme customization - spread all saved data to automatically include any fields
      setCustomization(prev => ({
        ...prev,
        ...saved, // Spread all saved customization fields
      }));

      // Load animations based on preset theme
      const presetTheme = saved.presetTheme || 'ocean-turtle';
      setAnimations(getThemeAnimations(presetTheme));
      if (saved.animationsEnabled !== undefined) setAnimationsEnabled(saved.animationsEnabled);

      // STEP 3: Cache for future visits (view-only cache for non-current users)
      if (!user.isCurrentUser && saved.primaryColor) {
        // Build theme to get computed colors
        const baseColors = {
          primaryColor: saved.primaryColor,
          secondaryColor: saved.secondaryColor,
          textColor: saved.textColor,
          accentColor: saved.accentColor,
          navbarBg: saved.navbarBg,
          tabBarBg: saved.tabBarBg,
          cardBg: saved.cardBg,
          appBg: saved.appBg,
          navbarTextColor: saved.navbarTextColor
        };

        // Apply color space transformation if needed
        const colorSpace = saved.colorSpace || 'Off';
        let computedColors = baseColors;

        if (colorSpace !== 'Off') {
          // Transform colors (similar to buildThemeFromCustomization)
          const options: ColorTransformOptions = {
            colorSpace,
            temperatureLimit: saved.temperatureLimit,
            magentaLimit: saved.magentaLimit,
            intensityLimit: saved.intensityLimit,
            redLimit: saved.redLimit,
            greenLimit: saved.greenLimit,
            blueLimit: saved.blueLimit,
            backgroundSaturationLimit: saved.backgroundSaturationLimit,
            backgroundBrightnessLimit: saved.backgroundBrightnessLimit,
            backgroundContrast: saved.backgroundContrast,
            backgroundHueGravity: saved.backgroundHueGravity,
            backgroundGraySaturation: saved.backgroundGraySaturation,
            backgroundDefaultHue: saved.backgroundDefaultHue
          };

          computedColors = {
            primaryColor: transformColor(baseColors.primaryColor, options),
            secondaryColor: transformColor(baseColors.secondaryColor, options),
            textColor: baseColors.textColor?.startsWith('#') ? transformColor(baseColors.textColor, options) : baseColors.textColor,
            accentColor: baseColors.accentColor?.startsWith('#') ? transformColor(baseColors.accentColor, options) : baseColors.accentColor,
            navbarBg: baseColors.navbarBg?.startsWith('#') ? transformColor(baseColors.navbarBg, options) : baseColors.navbarBg,
            tabBarBg: baseColors.tabBarBg?.startsWith('#') ? transformColor(baseColors.tabBarBg, options) : baseColors.tabBarBg,
            cardBg: baseColors.cardBg?.startsWith('#') ? transformColor(baseColors.cardBg, options) : baseColors.cardBg,
            appBg: baseColors.appBg?.startsWith('#') ? transformColor(baseColors.appBg, options) : baseColors.appBg,
            navbarTextColor: baseColors.navbarTextColor
          };
        }

        // Create and cache view-only theme
        const cacheData = {
          colors: computedColors,
          settings: saved,
          colorSpace: saved.colorSpace || 'Off',
          userId: user.id
        };

        cacheManager.set('profile', user.id, cacheData);
              }
    }

    // Mark theme as loaded
    setIsThemeLoaded(true);
  }, [user.id, user.isCurrentUser, user.profileCustomization]); // Run when user changes

  // Register profile theme with NavigationContext for LoadingScreen continuity
  // STEP 0: SYNCHRONOUS registration on mount (before any useEffects run)
  // This prevents ThemeContext from briefly showing site theme during reload
  React.useMemo(() => {
    const cached = cacheManager.get('profile', user.id);
    if (cached?.colors) {
      // CRITICAL: Set sessionStorage FIRST before calling setCurrentTheme
      // This ensures LoadingScreen sees the correct theme even during hard reload
      try {
        sessionStorage.setItem('currentPageTheme', JSON.stringify({
          type: 'profile',
          userId: user.id,
          colors: {
            primaryColor: cached.colors.primaryColor,
            secondaryColor: cached.colors.secondaryColor || cached.colors.primaryColor,
            textColor: cached.colors.textColor || '#ffffff',
            accentColor: cached.colors.accentColor || cached.colors.primaryColor
          }
        }));
      } catch (e) {
        console.warn('Failed to set currentPageTheme:', e);
      }

      // Now call setCurrentTheme to update NavigationContext
      setCurrentTheme({
        type: 'profile',
        userId: user.id,
        colors: {
          primaryColor: cached.colors.primaryColor,
          secondaryColor: cached.colors.secondaryColor || cached.colors.primaryColor,
          textColor: cached.colors.textColor || '#ffffff',
          accentColor: cached.colors.accentColor || cached.colors.primaryColor
        }
      });
    }
    return null;
  }, [user.id, setCurrentTheme]);

  // STEP 1: Re-register from cache in useEffect (redundant but ensures it persists)
  useEffect(() => {
    const cached = cacheManager.get('profile', user.id);
    if (cached?.colors) {
      setCurrentTheme({
        type: 'profile',
        userId: user.id,
        colors: {
          primaryColor: cached.colors.primaryColor,
          secondaryColor: cached.colors.secondaryColor || cached.colors.primaryColor,
          textColor: cached.colors.textColor || '#ffffff',
          accentColor: cached.colors.accentColor || cached.colors.primaryColor
        }
      });
          }
  }, [user.id, setCurrentTheme]);

  // STEP 2: Re-register when theme finishes loading (overwrites cache registration with fresh data)
  useEffect(() => {
    if (isThemeLoaded && localTheme.primaryColor) {
      setCurrentTheme({
        type: 'profile',
        userId: user.id,
        colors: {
          primaryColor: localTheme.primaryColor,
          secondaryColor: localTheme.secondaryColor || localTheme.primaryColor,
          textColor: localTheme.textColor || '#ffffff',
          accentColor: localTheme.accentColor || localTheme.primaryColor
        }
      });
          }
  }, [isThemeLoaded, localTheme.primaryColor, localTheme.secondaryColor, localTheme.textColor, localTheme.accentColor, user.id, setCurrentTheme, customization.colorSpace]);

  // Sync customization changes to profileCustomization
  useEffect(() => {
    setProfileCustomization(prev => ({
      ...prev,
      profileFrameColor: customization.profileFrameColor,
      profileFrameShape: customization.profileFrameShape,
      profileNameAlign: customization.profileNameAlign,
      profileFrameDesign: customization.profileFrameDesign,
    }));
  }, [customization.profileFrameColor, customization.profileFrameShape, customization.profileNameAlign, customization.profileFrameDesign]);

  // Validate posts alignment when card position changes
  useEffect(() => {
    const { cardPosition, postsAlignment } = profileCustomization;

    // If card is on the right and posts are on the left (conflict), change posts to center
    if (cardPosition === 'right' && postsAlignment === 'left') {
      setProfileCustomization(prev => ({ ...prev, postsAlignment: 'center' }));
    }

    // If card is on the left and posts are on the right (conflict), change posts to center
    if (cardPosition === 'left' && postsAlignment === 'right') {
      setProfileCustomization(prev => ({ ...prev, postsAlignment: 'center' }));
    }
  }, [profileCustomization.cardPosition, profileCustomization.postsAlignment]);

  // Sync currentSection when cardPosition changes
  useEffect(() => {
    setCurrentSection(mapCardPositionToSection(profileCustomization.cardPosition));
  }, [profileCustomization.cardPosition]);

  const [activeTab, setActiveTab] = useState('personal');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortBy, setSortBy] = useState<string>('newest');

  const sortOptions = [
    { value: 'newest', label: 'Newest', icon: Calendar },
    { value: 'oldest', label: 'Oldest', icon: Clock },
    { value: 'popular', label: 'Popular', icon: TrendingUp },
    { value: 'unpopular', label: 'Unpopular', icon: TrendingDown },
    { value: 'controversial', label: 'Most Controversial', icon: Flame }
  ];

  const currentSortOption = sortOptions.find(opt => opt.value === sortBy) || sortOptions[0];

  const [posts, setPosts] = useState(() => {
    // Debug: Log what we're receiving from backend
    if (user.posts.length > 0) {
    }

    return user.posts.map((p: any) => {
      const mapped = {
        id: p.id,
        title: p.title || 'Post',
        slug: p.slug,
        content: p.content,
        mediaUrl: p.mediaUrl,
        aboveContent: p.aboveContent,
        belowContent: p.belowContent,
        pinned: p.pinned || false,
        edited: false,
        createdAt: p.createdAt,
        likes: p.likes,
        dislikes: p.dislikes || 0,
        disliked: p.disliked || false,
        liked: p.liked || false,
        comments: p.comments,
        reposts: p.reposts || 0,
        replies: p.replies || [],
      };
      return mapped;
    });
  });

  const [communityPosts, setCommunityPosts] = useState<any[]>([]);
  const [replies, setReplies] = useState<any[]>([]);

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // Listen for post updates from other pages/components
  usePostSync({
    onLike: (data) => {
      setPosts(prev => prev.map(p =>
        p.id === data.postId
          ? { ...p, liked: data.liked, likes: data.likes }
          : p
      ));
    },
    onDislike: (data) => {
      setPosts(prev => prev.map(p =>
        p.id === data.postId
          ? { ...p, disliked: data.disliked, dislikes: data.dislikes }
          : p
      ));
    },
    postIds: posts.map(p => p.id)
  });

  // Listen for comment updates from other pages/components
  useCommentSync({
    onLike: (data) => {
      setPosts(prev => prev.map(p => {
        if (p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) => {
              // Handle both string and number IDs
              const replyId = String(r.id);
              const eventId = String(data.commentId);
              return replyId === eventId
                ? { ...r, liked: data.liked, likes: data.likes }
                : r;
            }),
          };
        }
        return p;
      }));
    },
    onDislike: (data) => {
      setPosts(prev => prev.map(p => {
        if (p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) => {
              // Handle both string and number IDs
              const replyId = String(r.id);
              const eventId = String(data.commentId);
              return replyId === eventId
                ? { ...r, disliked: data.disliked, dislikes: data.dislikes }
                : r;
            }),
          };
        }
        return p;
      }));
    },
  });

  // Notification synchronization for real-time updates
  useNotificationSync({
    onNew: (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onRead: (notificationId) => {
      setNotifications(prev => prev.map(n => n.id === notificationId ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    },
    onReadAll: () => {
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    },
    onDelete: (notificationId) => {
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    },
  });

  // Effects
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);

    // Listen for customizer open event from settings
    const handleOpenCustomizer = () => setShowCustomizer(true);
    window.addEventListener('openProfileCustomizer', handleOpenCustomizer);

    // Listen for profile theme updates from customizer (live preview)
    const handleThemeUpdate = (event: any) => {
      const { customization: newCustomization, animations: newAnimations, animationsEnabled: newAnimationsEnabled } = event.detail;

      if (newCustomization) {
        setCustomization(newCustomization);

        // Also update profileCustomization with alignment values
        setProfileCustomization(prev => ({
          ...prev,
          profileTitleAlign: newCustomization.profileTitleAlign || prev.profileTitleAlign,
          profileBioAlign: newCustomization.profileBioAlign || prev.profileBioAlign,
          cardPosition: newCustomization.cardPosition || prev.cardPosition,
          tabAlignment: newCustomization.tabAlignment || prev.tabAlignment,
          profilePicturePosition: newCustomization.profilePicturePosition || prev.profilePicturePosition,
          profilePictureSize: newCustomization.profilePictureSize || prev.profilePictureSize,
          postsAlignment: newCustomization.postsAlignment || prev.postsAlignment,
        }));
      }
      if (newAnimations) {
        setAnimations(newAnimations);
      }
      if (typeof newAnimationsEnabled === 'boolean') {
        setAnimationsEnabled(newAnimationsEnabled);
      }
    };
    window.addEventListener('profileThemeUpdated', handleThemeUpdate);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('openProfileCustomizer', handleOpenCustomizer);
      window.removeEventListener('profileThemeUpdated', handleThemeUpdate);
    };
  }, []);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  useEffect(() => {
    if (user.profileCustomization) {
      const saved = user.profileCustomization as Record<string, unknown>;

      // Add type guard
      if (typeof saved.animationsEnabled === 'boolean') {
        setAnimationsEnabled(saved.animationsEnabled);
      }

      // Add type guard
      if (saved.animations && typeof saved.animations === 'object') {
        setAnimations(saved.animations as ThemeAnimations);
      }
    }
  }, [user]);

  // Helper to convert hex to rgba (moved outside useEffect for reusability)
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
  };

  // Apply profile customization as CSS variables when customization changes
  // SKIP in mobileMode: ProfileClient is just one section in MobileShell, not the page owner.
  // Setting global CSS variables here would override the site theme for feed/other sections.
  useEffect(() => {
    if (mobileMode) return;

    // Use already-transformed colors from localTheme (no need to transform again)
    const transformedTextColor = localTheme.textColor;
    const transformedAccentColor = localTheme.accentColor;

    const root = document.documentElement;

    // Helper to blend two colors
    const blendColors = (color1: string, color2: string, ratio: number) => {
      const r1 = parseInt(color1.slice(1, 3), 16);
      const g1 = parseInt(color1.slice(3, 5), 16);
      const b1 = parseInt(color1.slice(5, 7), 16);

      const r2 = parseInt(color2.slice(1, 3), 16);
      const g2 = parseInt(color2.slice(3, 5), 16);
      const b2 = parseInt(color2.slice(5, 7), 16);

      const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
      const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
      const b = Math.round(b1 * (1 - ratio) + b2 * ratio);

      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    // Apply background gradient
    if (customization.gradientEnabled) {
      const ratio = (customization.gradientFavorability || 50) / 100;
      const blendedColor1 = blendColors(localTheme.primaryColor, localTheme.secondaryColor, ratio);
      const blendedColor2 = blendColors(localTheme.secondaryColor, localTheme.primaryColor, ratio);
      const strength = (customization.gradientStrength || 100) / 100;
      const finalColor1 = blendColors('#808080', blendedColor1, strength);
      const finalColor2 = blendColors('#808080', blendedColor2, strength);

      root.style.setProperty('--app-bg', `linear-gradient(${customization.gradientAngle || 135}deg, ${finalColor1}, ${finalColor2})`);
    } else {
      root.style.setProperty('--app-bg', localTheme.primaryColor);
    }

    // Apply other CSS variables using locally transformed colors
    // IMPORTANT: Variable names must match what's used in CSS!
    // CSS uses: var(--text-color) and var(--color-accent)
    root.style.setProperty('--text-color', transformedTextColor);
    root.style.setProperty('--color-accent', transformedAccentColor);

    // Use navbar and tab bar backgrounds from localTheme (already transformed and processed with opacity)
    root.style.setProperty('--navbar-bg', localTheme.navbarBg);
    root.style.setProperty('--tab-bar-bg', localTheme.tabBarBg);

    // Apply card background with gradient if enabled
    if (customization.cardGradientEnabled) {
      const ratio = (customization.cardGradientFavorability || 50) / 100;
      const blendedCard1 = blendColors(localTheme.cardGradientColor1 || localTheme.cardBg, localTheme.cardGradientColor2 || localTheme.cardBg, ratio);
      const blendedCard2 = blendColors(localTheme.cardGradientColor2 || localTheme.cardBg, localTheme.cardGradientColor1 || localTheme.cardBg, ratio);
      const strength = (customization.cardGradientStrength || 100) / 100;
      const finalCard1 = blendColors(localTheme.cardBg, blendedCard1, strength);
      const finalCard2 = blendColors(localTheme.cardBg, blendedCard2, strength);

      const gradientBg = `linear-gradient(${customization.cardGradientAngle || 120}deg, ${hexToRgba(finalCard1, customization.cardOpacity || 100)}, ${hexToRgba(finalCard2, customization.cardOpacity || 100)})`;
      root.style.setProperty('--card-bg', gradientBg);
    } else {
      root.style.setProperty('--card-bg', hexToRgba(localTheme.cardBg, customization.cardOpacity || 100));
    }
  }, [customization, localTheme, mobileMode]); // Depends on both customization and localTheme

  // Update follow status when user prop changes
  useEffect(() => {
    setIsFollowing(user.isFollowing || false);
    setIsMutualFollowers(user.isMutualFollowers || false);
  }, [user.isFollowing, user.isMutualFollowers]);

  // Fetch user's community posts
  useEffect(() => {
    async function fetchCommunityPosts() {
      try {
        const response = await fetchWithAuth(`/api/profile/${user.id}/community-posts`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setCommunityPosts((data.posts || []).map(normalizeLikeCounts));
        }
      } catch (error) {
      }
    }
    fetchCommunityPosts();
  }, [user.id]);

  // Fetch user's replies (both legacy comments and community post replies)
  useEffect(() => {
    async function fetchReplies() {
      try {
        const response = await fetchWithAuth(`/api/profile/${user.id}/replies`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setReplies((data.replies || []).map(normalizeLikeCounts));
        }
      } catch (error) {
      }
    }
    fetchReplies();
  }, [user.id]);

  // Focus animation effect - wheel-based post focusing
  useEffect(() => {
    if (!customization.focusAnimationEnabled) {
      setFocusedPostId(null);
      setFocusedPostIndex(-1);
      return;
    }

    if (posts.length === 0) return;

    const handleWheel = (e: WheelEvent) => {
      const direction = e.deltaY > 0 ? 1 : -1;
      const currentIndex = currentFocusIndexRef.current;

      // Check if we're at the edges
      const atTop = currentIndex === 0 && direction === -1;
      const atBottom = currentIndex === posts.length - 1 && direction === 1;

      // Allow normal scrolling at edges
      if (atTop || atBottom) {
        return;
      }

      e.preventDefault();

      // Update immediately without throttle
      const newIndex = currentIndex + direction;
      currentFocusIndexRef.current = newIndex;

      const focusedPost = posts[newIndex];
      if (focusedPost) {
        setFocusedPostId(focusedPost.id);
        setFocusedPostIndex(newIndex);
      }
    };

    // Initialize first post as focused (only if not already set)
    if (currentFocusIndexRef.current === -1 || currentFocusIndexRef.current >= posts.length) {
      currentFocusIndexRef.current = 0;
    }

    if (posts[currentFocusIndexRef.current]) {
      setFocusedPostId(posts[currentFocusIndexRef.current].id);
      setFocusedPostIndex(currentFocusIndexRef.current);
    }

    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [customization.focusAnimationEnabled, posts]);

  // Calculate post style based on distance from focused post (pyramid/octagon effect)
  const getPostStyle = (postIndex: number) => {
    if (!customization.focusAnimationEnabled || focusedPostIndex === -1) {
      // Focus animation is disabled or no post is focused
      return {
        width: '100%',
        maxWidth: isMobile ? '100%' : '50vw',
        marginBottom: '2rem',
        transform: 'scale(1)',
        opacity: 1,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      };
    }

    const distance = Math.abs(postIndex - focusedPostIndex);
    const baseWidth = isMobile ? window.innerWidth * 0.9 : window.innerWidth * 0.5; // 50vw base

    // Focused post is full base width
    if (distance === 0) {
      return {
        width: `${baseWidth}px`,
        maxWidth: `${baseWidth}px`,
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '2rem',
        transform: 'scale(1.05)',
        transformOrigin: 'center center',
        opacity: 1,
        willChange: 'transform, width, opacity',
        transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      };
    }

    // Even size reduction with 12% steps, 72% maximum
    const widthPercent = Math.max(72, 100 - (distance * 12));
    const calculatedWidth = (baseWidth * widthPercent) / 100;

    // Calculate scale - even scaling
    const scale = Math.max(0.90, 1 - (distance * 0.02));

    // Calculate opacity reduction - even steps
    const opacity = Math.max(0.5, 1 - (distance * 0.10));

    return {
      width: `${calculatedWidth}px`,
      maxWidth: `${calculatedWidth}px`,
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: '2rem',
      transform: `scale(${scale})`,
      transformOrigin: 'center center',
      opacity,
      willChange: 'transform, width, opacity',
      transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    };
  };

  // File handlers
  const handleProfilePicUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setProfilePic(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  }, []); // TODO: Add dependencies

  // Helper to apply opacity to dropdown colors
  const getDropdownButtonBg = () => {
    // Use dropdownColor (hex), or fall back to primaryColor (hex) - not tabBarBg which is already rgba
    const baseColor = localTheme.dropdownColor || localTheme.primaryColor;
    const opacity = customization.dropdownButtonOpacity ?? 100;
    return hexToRgba(baseColor, opacity);
  };

  const getDropdownMenuBg = () => {
    // Use dropdownColor (hex), or fall back to primaryColor (hex) - not tabBarBg which is already rgba
    const baseColor = localTheme.dropdownColor || localTheme.primaryColor;
    const opacity = customization.dropdownOpacity ?? 95;
    return hexToRgba(baseColor, opacity);
  };

  // Save all profile changes
  // Post actions
  const togglePost = (id: string, _field: 'pinned') => {
    if (!user.isCurrentUser && !isAdmin) return;

    setPosts(posts.map(post => {
      if (post.id === id) {
        return { ...post, pinned: !post.pinned };
      }
      return post;
    }));

    fetchWithAuth(`/api/posts/${id}/pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ pinned: !posts.find(p => p.id === id)?.pinned }),
    });
  };


  const createAdminPost = async () => {
    if (!isAdmin || !newAdminPost.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/profiles/${user.id}/admin-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          content: newAdminPost,
          pinned: true,
        }),
      });

      if (response.ok) {
        const newPost = await response.json();
        setPosts([{
          id: newPost.id,
          title: 'Admin Post',
          slug: newPost.slug,
          content: newPost.content,
          mediaUrl: undefined,
          aboveContent: undefined,
          belowContent: undefined,
          pinned: true,
          edited: false,
          createdAt: new Date(),
          likes: 0,
          dislikes: 0,
          disliked: false,
          liked: false,
          comments: 0,
          reposts: 0,
          replies: [],
        }, ...posts]);
        setNewAdminPost("");
      }
    } catch (error) {
    }
  };

  const editPost = (id: string, newContent: string) => {
    setPosts(posts.map(post =>
      post.id === id ? { ...post, content: newContent, edited: true } : post
    ));
  };

  const deletePost = (id: string) => {
    setPosts(posts.filter(post => post.id !== id));
  };

  const handleRepost = useCallback((postId: string) => {
    setRepostPostId(postId);
    setShowRepostModal(true);
  }, []);

  const handleLogout = useCallback(async () => {
    logout();
  }, [logout]);

  const handleSubmitRepost = useCallback(async () => {
    if (!currentUser || !repostPostId) return;

    setIsReposting(true);
    try {
      const response = await fetchWithAuth(`/api/posts/${repostPostId}/repost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment: repostComment || undefined })
      });

      if (response.ok) {
        setShowRepostModal(false);
        setRepostComment('');
        setRepostPostId(null);
        alert('Reposted successfully!');
      } else{
        const data = await response.json();
        alert(data.error || 'Failed to repost');
      }
    } catch (_error) {
      alert('Network error. Please try again.');
    }
    setIsReposting(false);
  }, []); // TODO: Add dependencies

  const handleShare = useCallback(async (postId: string) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;
    const url = post.slug && user.username
      ? `${window.location.origin}${getLegacyPostUrl(user.username, post.slug)}`
      : `${window.location.origin}/posts/${postId}`;

    // On mobile, try native share API first
    if (mobileMode && navigator.share) {
      try {
        await navigator.share({ title: post.title || 'Check out this post', url });
        fetchWithAuth(`/api/posts/${postId}/link-share`, { method: 'POST', credentials: 'include' }).catch(() => {});
        return;
      } catch { /* user cancelled, fall through to modal */ }
    }

    setSharePostId(postId);
    setShowShareModal(true);
  }, [posts, user.username, mobileMode]);

  const handleCopyLink = useCallback(async () => {
    if (!sharePostId) return;
    const post = posts.find(p => p.id === sharePostId);
    if (!post) return;

    const url = post.slug && user.username
      ? `${window.location.origin}${getLegacyPostUrl(user.username, post.slug)}`
      : `${window.location.origin}/posts/${sharePostId}`;

    try {
      await navigator.clipboard.writeText(url);
      fetchWithAuth(`/api/posts/${sharePostId}/link-share`, { method: 'POST', credentials: 'include' }).catch(() => {});
      setShowShareModal(false);
      setSharePostId(null);
    } catch (err) {
      // silent fail
    }
  }, [sharePostId, posts, user.username]);

  const handleCreatePost = useCallback(async (postData: {
    title: string;
    content: string;
    tags: string[];
    mediaUrl: string;
    mediaPosition: 'above' | 'below';
  }) => {
    try {
      // Backend exposes post creation at `POST /api/posts/` (see
      // EarthSocial backend `routes/posts.ts:95`). The earlier
      // `/api/users/:username/wall` URL never existed on the backend
      // — only callsite was here, hence the 404. Posts are attributed
      // to the authenticated user via the session cookie, so no
      // username path param is needed.
      const response = await fetchWithAuth(`/api/posts/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: postData.title.trim() || undefined,
          content: postData.content,
          tags: postData.tags,
          mediaUrl: postData.mediaUrl || undefined,
          mediaPosition: postData.mediaPosition,
        }),
      });

      if (response.ok) {
        const newPost = await response.json();
        // Add new post to the top of the posts list
        setPosts(prev => [newPost, ...prev]);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create post');
      }
    } catch (error) {
      alert('Failed to create post');
    }
  }, [user.username, setPosts]);

  const handleEditPost = useCallback((post: Post) => {
    setEditingPost(post);
    setShowModerateMenu(null);
  }, []); // TODO: Add dependencies

  const handleSaveEdit = useCallback(async (updatedData: { title?: string; content: string; mediaUrl?: string; removeMedia?: boolean }) => {
    if (!editingPost || !currentUser) return;

    const permUser: PermUser = {
      id: parseInt(currentUser.id),
      username: currentUser.username,
      isAdmin: isAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const editorType = getEditorType(editingPost, permUser);
    const endpoint = (editingPost as any).postType === 'community'
      ? `/api/moderation/communities/${(editingPost as any).communityId}/posts/${editingPost.id}`
      : `/api/moderation/posts/${editingPost.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...updatedData,
          moderatorEditReason: editorType !== 'user' ? 'Post edited by moderator' : undefined
        })
      });

      if (response.ok) {
        setPosts(prev => prev.map(p => p.id === editingPost.id ? {
          ...p,
          ...updatedData,
          editedBy: currentUser.id.toString(),
          editedByType: editorType,
          editedAt: new Date().toISOString()
        } as any : p));
        setEditingPost(null);
      }
    } catch (error) {
      throw error;
    }
  }, []); // TODO: Add dependencies

  const handlePinPost = useCallback(async (post: Post) => {
    if (!currentUser) return;

    const permUser: PermUser = {
      id: parseInt(currentUser.id),
      username: currentUser.username,
      isAdmin: isAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const pinnerType = getPinnerType(post, permUser);
    const endpoint = (post as any).postType === 'community'
      ? `/api/moderation/communities/${(post as any).communityId}/posts/${post.id}/pin`
      : `/api/moderation/posts/${post.id}/pin`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinned: data.pinned,
          pinnedBy: data.pinned ? currentUser.id.toString() : undefined,
          pinnedByType: data.pinned ? pinnerType : undefined,
          pinnedAt: data.pinned ? new Date().toISOString() : undefined
        } as any : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, []); // TODO: Add dependencies

  const handlePinToFeed = useCallback(async (post: Post) => {
    if (!(currentUser as any).isSuperAdmin) return;

    try {
      const response = await fetchWithAuth(`/api/moderation/posts/${post.id}/pin-to-feed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinnedToFeed: data.pinnedToFeed,
          pinnedToFeedAt: data.pinnedToFeed ? new Date().toISOString() : undefined
        } as any : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, []); // TODO: Add dependencies

  const handleDeletePost = useCallback(async (post: Post) => {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }

    const endpoint = (post as any).postType === 'community'
      ? `/api/moderation/communities/${(post as any).communityId}/posts/${post.id}`
      : `/api/moderation/posts/${post.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setPosts(prev => prev.filter(p => p.id !== post.id));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, []); // TODO: Add dependencies

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = useCallback((notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        // Use actorUsername instead of actorId
        if (notification.actorUsername) {
          navigate(`/u/${notification.actorUsername}`);
        } else if (notification.actorId) {
          navigate(`/user/${notification.actorId}`); // Fallback
        }
        break;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
      case 'LIKE':
      case 'DISLIKE':
      case 'REPOST':
        // Route based on postType and slug
        if (notification.postType === 'forum' && notification.communitySlug && notification.postSlug) {
          navigate(getForumPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'community' && notification.communitySlug && notification.postSlug) {
          navigate(getCommunityPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'legacy' && notification.postAuthorUsername && notification.postSlug) {
          navigate(getLegacyPostUrl(notification.postAuthorUsername, notification.postSlug));
        } else {
          navigate('/feed'); // Fallback
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
      case 'MESSAGE':
        navigate('/messages');
        break;
    }
  }, []); // TODO: Add dependencies

  const handleLikePost = useCallback(async (postId: string) => {
    // Find the post and capture initial state
    const targetPost = posts.find(p => p.id === postId);
    if (!targetPost) return;

    const wasLiked = targetPost.liked;
    const initialLikes = targetPost.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Optimistic update
    setPosts(prev => prev.map(post => {
      if (post.id === postId) {
        return { ...post, liked: newLiked, likes: newLikes };
      }
      return post;
    }));

    // Emit local event for same-page sync
    postEventBus.emitLike(postId, newLiked, newLikes);

    try {
      await fetchWithAuth(`/api/posts/${postId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch (error) {
      // Revert on error
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return { ...post, liked: wasLiked, likes: initialLikes };
        }
        return post;
      }));
      // Emit revert event
      postEventBus.emitLike(postId, wasLiked, initialLikes);
    }
  }, [posts]);

  const handleDislikePost = useCallback(async (postId: string) => {
    // Find the post and capture initial state
    const targetPost = posts.find(p => p.id === postId);
    if (!targetPost) return;

    const wasDisliked = targetPost.disliked;
    const initialDislikes = targetPost.dislikes;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    // Optimistic update
    setPosts(prev => prev.map(post => {
      if (post.id === postId) {
        return { ...post, disliked: newDisliked, dislikes: newDislikes };
      }
      return post;
    }));

    // Emit local event for same-page sync
    postEventBus.emitDislike(postId, newDisliked, newDislikes);

    try {
      await fetchWithAuth(`/api/posts/${postId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch (error) {
      // Revert on error
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return { ...post, disliked: wasDisliked, dislikes: initialDislikes };
        }
        return post;
      }));
      // Emit revert event
      postEventBus.emitDislike(postId, wasDisliked, initialDislikes);
    }
  }, [posts]);

  const likeComment = async (commentId: string, postId: string) => {
    const post = posts.find(p => p.id === postId);
    if (!post || !post.replies) return;

    const comment = post.replies.find((r: any) => r.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialLiked = comment.liked || false;
    const initialLikes = comment.likes || 0;

    // Calculate new state
    const newLiked = !initialLiked;
    const newLikes = initialLiked ? initialLikes - 1 : initialLikes + 1;

    // Optimistic update
    setPosts(prev => prev.map(p => {
      if (p.id === postId && p.replies) {
        return {
          ...p,
          replies: p.replies.map((r: any) =>
            r.id === commentId
              ? { ...r, liked: newLiked, likes: newLikes }
              : r
          ),
        };
      }
      return p;
    }));

    // Emit event for same-page sync
    commentEventBus.emitLike(parseInt(commentId), newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/comments/${commentId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setPosts(prev => prev.map(p => {
          if (p.id === postId && p.replies) {
            return {
              ...p,
              replies: p.replies.map((r: any) =>
                r.id === commentId
                  ? { ...r, liked: initialLiked, likes: initialLikes }
                  : r
              ),
            };
          }
          return p;
        }));
        // Emit revert event
        commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
      }
    } catch (error) {
      // Revert on error
      setPosts(prev => prev.map(p => {
        if (p.id === postId && p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) =>
              r.id === commentId
                ? { ...r, liked: initialLiked, likes: initialLikes }
                : r
            ),
          };
        }
        return p;
      }));
      // Emit revert event
      commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
    }
  };

  const dislikeComment = async (commentId: string, postId: string) => {
    const post = posts.find(p => p.id === postId);
    if (!post || !post.replies) return;

    const comment = post.replies.find((r: any) => r.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialDisliked = comment.disliked || false;
    const initialDislikes = comment.dislikes || 0;

    // Calculate new state
    const newDisliked = !initialDisliked;
    const newDislikes = initialDisliked ? initialDislikes - 1 : initialDislikes + 1;

    // Optimistic update
    setPosts(prev => prev.map(p => {
      if (p.id === postId && p.replies) {
        return {
          ...p,
          replies: p.replies.map((r: any) =>
            r.id === commentId
              ? { ...r, disliked: newDisliked, dislikes: newDislikes }
              : r
          ),
        };
      }
      return p;
    }));

    // Emit event for same-page sync
    commentEventBus.emitDislike(parseInt(commentId), newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/comments/${commentId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setPosts(prev => prev.map(p => {
          if (p.id === postId && p.replies) {
            return {
              ...p,
              replies: p.replies.map((r: any) =>
                r.id === commentId
                  ? { ...r, disliked: initialDisliked, dislikes: initialDislikes }
                  : r
              ),
            };
          }
          return p;
        }));
        // Emit revert event
        commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
      }
    } catch (error) {
      // Revert on error
      setPosts(prev => prev.map(p => {
        if (p.id === postId && p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) =>
              r.id === commentId
                ? { ...r, disliked: initialDisliked, dislikes: initialDislikes }
                : r
            ),
          };
        }
        return p;
      }));
      // Emit revert event
      commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
    }
  };

  // Memoize bubble positions to prevent re-rendering on every state change
  const bubblePositions = React.useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => ({
      id: `bubble-${i}`,
      top: Math.random() * 100,
      left: Math.random() * 100,
      duration: 3 + i
    }));
  }, []); // Empty deps - only generate once on mount

  // Extract specific color for memoization
  const accentColorForDecoration = localTheme.accentColor;

  const ProfileFrameDesign = React.useMemo(() => {
    if (profileDecoration) {
      return (
        <div className="absolute inset-0 relative z-50">
        <img
        src={profileDecoration}
        alt="Custom decoration"
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        />
        </div>
      );
    }

    switch (profileCustomization.profileFrameDesign) {
      case 'waves':
        return (
          <div className="absolute -bottom-3 left-0 right-0 flex justify-center z-50">
            <svg width="140" height="20" viewBox="0 0 140 20">
              <path d="M0,10 Q35,0 70,10 T140,10" stroke={localTheme.profileFrameColor} strokeWidth="3" fill="none" />
            </svg>
          </div>
        );
      case 'bubbles':
        return (
          <>
            {bubblePositions.map((bubble) => (
              <div
                key={bubble.id}
                className="absolute rounded-full z-50"
                style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: accentColorForDecoration,
                  top: `${bubble.top}%`,
                  left: `${bubble.left}%`,
                  animation: `float ${bubble.duration}s infinite ease-in-out`
                }}
              />
            ))}
          </>
        );
      default:
        return (
          <div className="absolute -left-4 -right-4 z-50" style={{ height: '80%', bottom: '-2.5rem' }}>
            <img
              src={CoralFrameSvg}
              alt="Coral frame decoration"
              className="absolute bottom-0 w-full pointer-events-none"
              style={{
                filter: `hue-rotate(${accentColorForDecoration ? '0deg' : '0deg'})`,
                objectFit: 'cover',
                objectPosition: 'bottom center',
                transform: 'scale(1.2)'
              }}
            />
          </div>
        );
    }
  }, [profileDecoration, profileCustomization.profileFrameDesign, localTheme.profileFrameColor, accentColorForDecoration, bubblePositions]);

  // Toggle handlers - must be before conditional return
  const handleToggleShowSortMenu = useCallback(() => {
    setShowSortMenu(prev => !prev);
  }, []);
  const handleToggleShowCustomizer = useCallback(() => {
    setShowCustomizer(prev => !prev);
  }, []);
  const handleToggleShowModerationModal = useCallback(() => {
    setShowModerationModal(prev => !prev);
  }, []);
  const handleCloseShowRepostModal = useCallback(() => {
    setShowRepostModal(false);
  }, []);
  const handleCloseShowShareModal = useCallback(() => {
    setShowShareModal(false);
  }, []);
  const handleCloseShowPostWidget = useCallback(() => {
    setShowPostWidget(false);
  }, []);
  const handleCloseShowNotifications = useCallback(() => {
    setShowNotifications(false);
  }, []);
  const handleCloseShowSortMenu = useCallback(() => {
    setShowSortMenu(false);
  }, []);
  const handleCloseShowCustomizer = useCallback(() => {
    setShowCustomizer(false);
  }, []);
  const handleCloseShowModerationModal = useCallback(() => {
    setShowModerationModal(false);
  }, []);
  const handleCloseShowModerateMenu = useCallback(() => {
    setShowModerateMenu(null);
  }, []);
  const handleOpenShowCustomizer = useCallback(() => {
    setShowCustomizer(true);
  }, []);
  const handleOpenShowRepostModal = useCallback(() => {
    setShowRepostModal(true);
  }, []);
  const handleOpenShowShareModal = useCallback(() => {
    setShowShareModal(true);
  }, []);
  const handleOpenShowPostWidget = useCallback(() => {
    setShowPostWidget(true);
  }, []);

  // Show loading state until theme is loaded
  if (!isThemeLoaded) {
    // Pass the customization background to prevent flash
    const loadingBackground = customization.gradientEnabled
      ? `linear-gradient(${customization.gradientAngle || 135}deg, ${localTheme.primaryColor}, ${localTheme.secondaryColor})`
      : localTheme.primaryColor;
    return <LoadingScreen background={loadingBackground} textColor={localTheme.textColor} />;
  }

  // ─── Render helpers for content extraction ───

  /** Modals rendered in both mobile and desktop layouts */
  const renderModals = () => {
    const modals = (
    <>
      {/* Theme Customizer Modal - use lightweight mobile version in mobileMode */}
      {mobileMode ? (
        <MobileProfileThemeCustomizer
          isOpen={showCustomizer && !!user.isCurrentUser}
          onClose={() => { requestAnimationFrame(() => setShowCustomizer(false)); }}
          userId={user.id}
        />
      ) : (
        <ProfileThemeCustomizer
          isOpen={showCustomizer && !!user.isCurrentUser}
          onClose={() => { requestAnimationFrame(() => setShowCustomizer(false)); }}
          userId={user.id}
          initialCustomization={customization}
          initialAnimations={animations}
          initialAnimationsEnabled={animationsEnabled}
          initialAvatar={profilePic}
        />
      )}

      {/* Post Creation Widget */}
      <ProfilePostModal
        isOpen={showPostWidget}
        onClose={() => setShowPostWidget(false)}
        onSubmit={handleCreatePost}
        currentUser={currentUser || null}
        profileUser={user}
        theme={localTheme}
        scale={scale}
        scaleW={scaleW}
        scaleH={scaleH}
        getCardBackground={getCardBackground}
        customization={customization}
        cardPosition={profileCustomization.cardPosition}
      />

      {/* Repost Modal */}
      {showRepostModal && (
        <div
          ref={repostModalRef}
          className="fixed z-[100] max-w-md w-full mx-4"
          style={{
            left: '50%',
            top: '5rem',
            transform: `translate(calc(-50% + ${repostModalPosition.x}px), ${repostModalPosition.y}px)`,
            cursor: isDragging ? 'grabbing' : 'default'
          }}
          onMouseMove={(e) => {
            if (isDragging) {
              const deltaX = e.clientX - dragStart.x;
              const deltaY = e.clientY - dragStart.y;
              setRepostModalPosition({ x: repostModalPosition.x + deltaX, y: repostModalPosition.y + deltaY });
              setDragStart({ x: e.clientX, y: e.clientY });
            }
          }}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
        >
          <div className="rounded-lg backdrop-blur-sm border shadow-2xl" style={{
            background: localTheme.cardBg.replace(/[\d.]+\)$/, '0.95)'),
            borderColor: localTheme.accentColor + '40'
          }}>
            <div
              className="flex items-center justify-between mb-4 p-6 pb-2 cursor-grab active:cursor-grabbing"
              onMouseDown={(e) => {
                setIsDragging(true);
                setDragStart({ x: e.clientX, y: e.clientY });
              }}
              style={{
                borderBottom: `1px solid ${localTheme.accentColor}20`,
                userSelect: 'none'
              }}
            >
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Repost</h3>
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostModalPosition({ x: 0, y: 0 });
                }}
                className="transition-colors hover:opacity-70"
                style={{ color: localTheme.textColor }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <X size={24} />
              </button>
            </div>
            <div className="px-6 pb-6">
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Add an optional comment to your repost:</p>
            <textarea
              value={repostComment}
              onChange={(e) => setRepostComment(e.target.value)}
              placeholder="What do you think about this?"
              className="w-full p-3 rounded-lg outline-none resize-none transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
              rows={4}
              maxLength={280}
            />
            <div className="text-right text-sm mb-4" style={{ color: localTheme.textColor + '99' }}>{repostComment.length}/280</div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostComment('');
                }}
                className="flex-1 py-2 rounded-lg transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRepost}
                disabled={isReposting}
                className="flex-1 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                {isReposting ? 'Reposting...' : 'Repost'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] max-w-md w-full mx-4">
          <div className="rounded-lg p-6 backdrop-blur-sm border shadow-2xl" style={{
            background: localTheme.cardBg.replace(/[\d.]+\)$/, '0.95)'),
            borderColor: localTheme.accentColor + '40'
          }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Share Post</h3>
              <button onClick={handleCloseShowShareModal} className="transition-colors hover:opacity-70" style={{ color: localTheme.textColor }}>
                <X size={24} />
              </button>
            </div>
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Share this post:</p>
            <button
              onClick={handleCopyLink}
              className="w-full py-3 rounded-lg transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
            >
              <Link2 size={18} />
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Moderation Modal */}
      {showModerationModal && (currentUser && ((currentUser as any).isSiteModerator || (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || isAdmin)) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={handleCloseShowModerationModal}
        >
          <div
            className="rounded-lg backdrop-blur-sm border p-6 w-[600px] max-h-[80vh] overflow-y-auto"
            style={{
              background: theme.cardBg?.replace(/[\d.]+\)$/, '0.95)') || 'rgba(255, 255, 255, 0.95)',
              borderColor: localTheme.accentColor + '40'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Shield size={24} style={{ color: localTheme.accentColor }} />
                <h2 className="text-2xl font-bold" style={{ color: localTheme.textColor }}>
                  Moderate Profile
                </h2>
              </div>
              <button
                onClick={handleCloseShowModerationModal}
                className="p-2 hover:bg-white/10 rounded transition-colors"
                style={{ color: localTheme.textColor + '99' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Moderator Features */}
              <div className="space-y-4 pb-4" style={{ borderBottom: `1px solid ${localTheme.accentColor}20` }}>
                <p className="text-sm font-semibold" style={{ color: localTheme.accentColor }}>
                  Moderator Features
                </p>

                {/* Profile Name */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                    Profile Name
                  </label>
                  <input
                    type="text"
                    value={modProfileName}
                    onChange={(e) => setModProfileName(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: localTheme.textColor,
                      border: `1px solid ${localTheme.accentColor}40`
                    }}
                  />
                </div>

                {/* Profile Title */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                    Profile Title
                  </label>
                  <input
                    type="text"
                    value={modProfileTitle}
                    onChange={(e) => setModProfileTitle(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: localTheme.textColor,
                      border: `1px solid ${localTheme.accentColor}40`
                    }}
                  />
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                    Bio
                  </label>
                  <textarea
                    value={modProfileBio}
                    onChange={(e) => setModProfileBio(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg outline-none resize-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: localTheme.textColor,
                      border: `1px solid ${localTheme.accentColor}40`,
                      minHeight: '100px'
                    }}
                  />
                </div>
              </div>

              {/* Admin Features */}
              {isAdmin && (
                <div className="space-y-4 pt-4">
                  <p className="text-sm font-semibold" style={{ color: '#ef4444' }}>
                    Admin Features
                  </p>

                  {/* Username */}
                  <div>
                    <label className="block text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                      Username
                    </label>
                    <input
                      type="text"
                      value={modUsername}
                      onChange={(e) => setModUsername(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg outline-none"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: localTheme.textColor,
                        border: `1px solid #ef444440`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModerationModal(false);
                  // Reset values
                  setModProfileName(user.displayName || user.username);
                  setModProfileTitle(user.title || 'Earthling');
                  setModProfileBio(user.bio || DEFAULT_BIO);
                  setModUsername(user.username);
                }}
                className="flex-1 px-4 py-3 rounded-lg font-semibold transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const updates: any = {
                      profileName: modProfileName,
                      profileTitle: modProfileTitle,
                      bio: modProfileBio,
                    };

                    if (isAdmin && modUsername !== user.username) {
                      updates.username = modUsername;
                    }

                    const response = await fetchWithAuth(`/api/moderation/users/${user.id}/profile`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      credentials: 'include',
                      body: JSON.stringify(updates),
                    });

                    if (!response.ok) {
                      const error = await response.json();
                      throw new Error(error.error || 'Failed to update profile');
                    }

                    const data = await response.json();
                    alert('Profile moderation changes saved successfully');
                    setShowModerationModal(false);
                    window.location.reload();
                  } catch (error) {
                    alert(error instanceof Error ? error.message : 'Failed to save changes');
                  }
                }}
                className="flex-1 px-4 py-3 rounded-lg font-semibold transition-all hover:opacity-90"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
    // On mobile, render modals in a portal to avoid SubPanelSwiper interference
    return mobileMode ? createPortal(modals, document.body) : modals;
  };

  /** Center column: profile card + admin post + tabs + posts */
  const renderCenterContent = () => (
    <>
      {/* Profile Header with Card Position */}
      <div className="flex w-full" style={{
        justifyContent: 'center'
      }}>
      <div className="rounded-2xl relative" style={{
        background: getCardBackground(localTheme, customization, theme.cardBg),
        width: '100%',
        padding: `${scaleH(16, scale.h)}px ${scaleW(16, scale.w)}px`,
        marginBottom: `${scaleH(24, scale.h)}px`
      }}>
        <div className="flex flex-col" style={{
          alignItems: 'flex-start',
          gap: `${scaleH(16, scale.h)}px`
        }}>
          {/* Profile Picture with Frame */}
          <div className="relative">
            <div className="relative">
              <div
                className="relative overflow-hidden pixelated"
                style={{
                  width: `${scaleW(96, scale.w)}px`,
                  height: `${scaleW(96, scale.w)}px`,
                  border: `${scaleW(4, scale.w)}px solid`,
                  borderColor: localTheme.profileFrameColor,
                  borderRadius: profileCustomization.profileFrameShape === 'circle' ? '50%' : `${scaleW(12, scale.w)}px`
                }}
              >
                {profilePic ? (
                  <img
                    src={profilePic.startsWith('/api/') ? profilePic : getMediaUrl(profilePic)}
                    alt="Profile"
                    className="w-full h-full object-cover pixelated"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ backgroundColor: theme.cardBg }}
                  >
                    <PixelatedTurtle key={`turtle-${localTheme.turtleColor}`} size={Math.round(scaleW(40, scale.w))} color={localTheme.turtleColor} />
                  </div>
                )}
              </div>

              {/* overlay decoration */}
              {ProfileFrameDesign}
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex-1 w-full">
            {/* Name */}
            <div className="flex items-center mb-2 gap-3">
              <h1
                className="font-bold"
                style={{
                  color: localTheme.textColor,
                  fontSize: `${scaleW(24, scale.w)}px`
                }}
              >
                {profileName}
              </h1>
              {!user.isCurrentUser && (
                <button
                  onClick={async () => {
                    try {
                      const { followUser, unfollowUser } = await import('../../lib/api-client');
                      if (isFollowing) {
                        await unfollowUser(user.id);
                        setIsFollowing(false);
                      } else {
                        await followUser(user.id);
                        setIsFollowing(true);
                      }
                      window.dispatchEvent(new CustomEvent('followingUpdated'));
                      onFollowChange?.();
                    } catch (error) {
                    }
                  }}
                  className="rounded-lg font-semibold transition-all hover:opacity-80"
                  style={{
                    paddingLeft: `${scaleW(20, scale.w)}px`,
                    paddingRight: `${scaleW(20, scale.w)}px`,
                    paddingTop: `${scaleH(8, scale.h)}px`,
                    paddingBottom: `${scaleH(8, scale.h)}px`,
                    fontSize: `${scaleW(15, scale.w)}px`,
                    minWidth: `${scaleW(112, scale.w)}px`,
                    backgroundColor: isFollowing ? 'rgba(255, 255, 255, 0.1)' : localTheme.accentColor,
                    color: localTheme.textColor,
                    border: `1px solid ${isFollowing ? localTheme.accentColor : 'transparent'}`,
                    opacity: isContentUpdating ? 0.5 : 1,
                    transition: 'opacity 0.15s ease'
                  }}
                >
                  {isContentUpdating ? 'Updating...' : (isMutualFollowers ? 'Friends' : isFollowing ? 'Following' : 'Follow')}
                </button>
              )}
            </div>

            {/* Profile Title */}
            <p className="opacity-90" style={{
              color: localTheme.textColor,
              fontSize: `${scaleW(18, scale.w)}px`,
              marginBottom: `${scaleH(12, scale.h)}px`
            }}>
              {profileTitle}
            </p>

            {/* Profile Bio */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: `${scaleH(8, scale.h)}px` }}>
              {profileBio.split('\n').filter((line: string) => line.trim()).map((line: string, i: number) => (
                <p key={i} className="italic opacity-85" style={{
                  color: localTheme.textColor,
                  fontSize: `${scaleW(16, scale.w)}px`
                }}>
                  {line}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Custom Header Design */}
        {headerDesign && (
          <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none relative">
            <img
              src={headerDesign}
              alt="Header design"
              className="absolute inset-0 w-full h-full object-contain"
            />
          </div>
        )}
      </div>
      </div>

      {/* Admin Post Creation */}
      {isAdmin && !user.isCurrentUser && (
        <div className="rounded-xl p-4 mb-6" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
          <div className="flex items-center gap-2 mb-3">
            <Shield size={20} style={{ color: localTheme.accentColor }} />
            <h3 className="font-semibold" style={{ color: localTheme.textColor }}>
              Admin Post
            </h3>
          </div>
          <div className="flex gap-3">
            <textarea
              value={newAdminPost}
              onChange={(e) => setNewAdminPost(e.target.value)}
              placeholder="Create an admin post for this user's profile..."
              className="flex-1 p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 resize-none"
              rows={3}
            />
            <button
              onClick={createAdminPost}
              disabled={!newAdminPost.trim()}
              className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              style={{ backgroundColor: localTheme.accentColor, color: localTheme.textColor }}
            >
              Post & Pin
            </button>
          </div>
        </div>
      )}

      {/* Tab Bar */}
      <div className="flex w-full" style={{
        justifyContent: 'flex-start',
        marginTop: '-1.5rem',
        overflow: 'visible'
      }}>
        <div style={{ width: '100%', overflow: 'visible' }}>
          <div className="flex items-center overflow-x-auto" style={{
            justifyContent: mobileMode ? 'space-between' : 'flex-start',
            marginBottom: mobileMode ? '16px' : `${scaleH(32, scale.h)}px`,
            gap: mobileMode ? '6px' : `${scaleW(12, scale.w)}px`,
            position: 'relative',
            zIndex: 30,
            overflowY: 'visible'
          }}>
            {/* Tabs */}
            {['Personal', 'Media', 'Community', 'Replies'].map((tab) => (
              <button
                key={tab}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveTab(tab.toLowerCase());
                }}
                className="rounded-xl font-semibold transition-all pixelated whitespace-nowrap"
                style={{
                  backgroundColor: activeTab === tab.toLowerCase()
                    ? localTheme.accentColor
                    : localTheme.tabBarBg,
                  color: localTheme.textColor,
                  border: `2px solid ${activeTab === tab.toLowerCase() ? localTheme.accentColor : 'transparent'}`,
                  paddingLeft: mobileMode ? '10px' : `${scaleW(24, scale.w)}px`,
                  paddingRight: mobileMode ? '10px' : `${scaleW(24, scale.w)}px`,
                  paddingTop: mobileMode ? '6px' : `${scaleH(12, scale.h)}px`,
                  paddingBottom: mobileMode ? '6px' : `${scaleH(12, scale.h)}px`,
                  fontSize: mobileMode ? '12px' : `${scaleW(14, scale.w)}px`,
                }}
              >
                {tab}
              </button>
            ))}

            {/* Sort Dropdown */}
            <div className="relative" style={{ zIndex: 41 }}>
              <button
                onClick={handleToggleShowSortMenu}
                className="flex items-center rounded-lg font-medium transition-all"
                style={{
                  backgroundColor: getDropdownButtonBg(),
                  border: 'none',
                  color: localTheme.textColor,
                  gap: `${scaleW(8, scale.w)}px`,
                  paddingLeft: mobileMode ? '8px' : `${scaleW(16, scale.w)}px`,
                  paddingRight: mobileMode ? '8px' : `${scaleW(16, scale.w)}px`,
                  paddingTop: mobileMode ? '4px' : `${scaleH(8, scale.h)}px`,
                  paddingBottom: mobileMode ? '4px' : `${scaleH(8, scale.h)}px`,
                  fontSize: mobileMode ? '11px' : `${scaleW(14, scale.w)}px`
                }}
              >
                <currentSortOption.icon size={mobileMode ? 14 : Math.round(scaleW(16, scale.w))} />
                {!mobileMode && <span>{currentSortOption.label}</span>}
                <ChevronDown size={mobileMode ? 14 : Math.round(scaleW(16, scale.w))} className={showSortMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>

              {showSortMenu && (
                <>
                  <div
                    className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl"
                    style={{
                      backgroundColor: getDropdownMenuBg(),
                      borderColor: localTheme.textColor,
                      border: '2px solid',
                      zIndex: 42,
                      marginTop: `${scaleH(8, scale.h)}px`,
                      minWidth: `${scaleW(160, scale.w)}px`
                    }}
                  >
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value);
                          setShowSortMenu(false);
                        }}
                        className="flex items-center w-full text-left font-medium transition-all"
                        style={{
                          color: option.value === sortBy ? localTheme.textColor : localTheme.textColor,
                          backgroundColor: option.value === sortBy ? localTheme.highlightColor : 'transparent',
                          gap: `${scaleW(8, scale.w)}px`,
                          paddingLeft: `${scaleW(16, scale.w)}px`,
                          paddingRight: `${scaleW(16, scale.w)}px`,
                          paddingTop: `${scaleH(12, scale.h)}px`,
                          paddingBottom: `${scaleH(12, scale.h)}px`,
                          fontSize: `${scaleW(14, scale.w)}px`
                        }}
                        onMouseEnter={(e) => {
                          if (option.value !== sortBy) {
                            e.currentTarget.style.backgroundColor = localTheme.highlightColor + '40';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (option.value !== sortBy) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <option.icon size={Math.round(scaleW(16, scale.w))} />
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div
                    className="fixed inset-0"
                    style={{ zIndex: 40 }}
                    onClick={handleCloseShowSortMenu}
                    onTouchStart={handleCloseShowSortMenu}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  /** Share/Post button content for left or right panel */
  const renderShareButton = () => (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        requestAnimationFrame(() => {
          setIsShareAnimating(true);
          setTimeout(() => {
            setShowPostWidget(true);
            setIsShareAnimating(false);
          }, 300);
        });
      }}
      className="rounded-lg shadow-lg font-semibold overflow-hidden w-full"
      style={{
        backgroundColor: localTheme.accentColor,
        color: localTheme.textColor,
        padding: '0.75rem 1.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        transform: showPostWidget ? 'scale(1.02)' : 'scale(1)',
        minHeight: '48px',
      }}
    >
      <span style={{
        display: 'inline-block',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isShareAnimating ? 0 : 1,
        transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
      }}>
        {showPostWidget ? 'Post' : 'Share'}
      </span>
    </button>
  );

  // ─── Mobile Layout ───
  if (mobileMode) {
    return (
      <div style={{ background: getBackground(localTheme, customization, theme.appBg || '#0891b2'), height: '100%', overflow: 'hidden' }}>
        <SubPanelSwiper
          activePanel={{ left: 0, middle: 1, right: 2 }[currentSection]}
          onPanelChange={(i: number) => {
            const section = (['left', 'middle', 'right'] as const)[i];
            setCurrentSection(section);
          }}
          screenWidth={mobileScreenWidth}
        >
          {[
            <div key="left" className="pt-6 px-4 h-full overflow-auto flex flex-col items-stretch gap-3" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top) + 1rem)' }}>
              {renderShareButton()}
              <p className="text-xs text-center" style={{ color: localTheme.textColor, opacity: 0.4 }}>
                Tap to create a post
              </p>
            </div>,
            <div key="center" className="flex flex-col items-center w-full h-full overflow-y-auto overscroll-contain px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
              {renderCenterContent()}
              {/* Posts area reusing desktop posts section - simplified for mobile */}
              <div className="w-full" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                // Room for the sort dropdown when there are no posts (panel is a scroll container)
                minHeight: `${sortOptions.length * 48 + 80}px`
              }}>
                <div style={{
                  width: '100%',
                  maxWidth: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch'
                }}>
                  {/* Posts - Personal Tab */}
                  {activeTab === 'personal' && (
                    <div className="w-full" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: '1rem'
                    }}>
                    {(() => {
                      let sortedPosts = [...posts];
                      switch(sortBy) {
                        case 'newest':
                          sortedPosts = sortedPosts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                          break;
                        case 'oldest':
                          sortedPosts = sortedPosts.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                          break;
                        case 'popular':
                          sortedPosts = sortedPosts.sort((a, b) => b.likes - a.likes);
                          break;
                        case 'unpopular':
                          sortedPosts = sortedPosts.sort((a, b) => (b.dislikes || 0) - (a.dislikes || 0));
                          break;
                        case 'controversial':
                          sortedPosts = sortedPosts.sort((a, b) => {
                            const aC = Math.min(a.likes, a.dislikes || 0);
                            const bC = Math.min(b.likes, b.dislikes || 0);
                            return bC - aC;
                          });
                          break;
                      }

                      return sortedPosts.map((post) => (
                        <div
                          key={post.id}
                          className="rounded-xl relative"
                          style={{
                            background: getCardBackground(localTheme, customization, theme.cardBg),
                            padding: '1rem',
                            marginBottom: '0.5rem',
                          }}>
                          <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

                          {/* Timestamp */}
                          <div className="flex justify-end" style={{ marginBottom: '0.5rem' }}>
                            <div className="text-xs opacity-60" style={{ color: localTheme.textColor }}>
                              {new Date(post.createdAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                            </div>
                          </div>

                          {post.title && (
                            <Link
                              to={post.slug && user.username ? getLegacyPostUrl(user.username, post.slug) : `/posts/${post.id}`}
                              state={{ from: 'profile', authorId: user.id }}
                              className="hover:opacity-80 transition-opacity cursor-pointer"
                            >
                              <h2 className="text-lg font-bold mb-2" style={{ color: localTheme.textColor }}>
                                {post.title}
                              </h2>
                            </Link>
                          )}

                          {/* Media above content */}
                          {post.aboveContent && post.mediaUrl && (
                            <div className="mb-3 rounded-lg overflow-hidden">
                              <img src={post.mediaUrl} alt="Post media" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} />
                            </div>
                          )}

                          <Link
                            to={post.slug && user.username ? getLegacyPostUrl(user.username, post.slug) : `/posts/${post.id}`}
                            state={{ from: 'profile', authorId: user.id }}
                            className="block hover:opacity-80 transition-opacity cursor-pointer"
                          >
                            <p className="leading-relaxed opacity-90 text-sm" style={{ color: localTheme.textColor }}>
                              {post.content}
                            </p>
                          </Link>

                          {/* Media below content */}
                          {post.belowContent && post.mediaUrl && (
                            <div className="mt-3 rounded-lg overflow-hidden">
                              <img src={post.mediaUrl} alt="Post media" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} />
                            </div>
                          )}

                          {/* Post Actions */}
                          <div className="flex items-center gap-4 pt-3 mt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <button onClick={() => handleLikePost(post.id)} className="flex items-center gap-1" style={{ color: (post as any).liked ? '#ef4444' : `${localTheme.textColor}99` }}>
                              <Heart size={16} fill={(post as any).liked ? '#ef4444' : 'none'} />
                              <span className="text-xs">{post.likes}</span>
                            </button>
                            <button onClick={() => handleDislikePost(post.id)} className="flex items-center gap-1" style={{ color: (post as any).disliked ? '#f97316' : `${localTheme.textColor}99` }}>
                              <ThumbsDown size={16} fill={(post as any).disliked ? '#f97316' : 'none'} />
                              <span className="text-xs">{post.dislikes}</span>
                            </button>
                            <button onClick={() => navigate(`/posts/${post.id}`)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                              <MessageSquare size={16} />
                              <span className="text-xs">{post.comments}</span>
                            </button>
                            <button onClick={() => handleRepost(post.id)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                              <Repeat2 size={16} />
                              <span className="text-xs">{post.reposts || 0}</span>
                            </button>
                            <button onClick={() => handleShare(post.id)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                              <Share2 size={16} />
                            </button>
                          </div>

                          {/* Comment Previews */}
                          {(post as any).replies && (post as any).replies.length > 0 && (() => {
                            const previewComments = selectPreviewComments((post as any).replies);
                            return (
                              <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                                <div className="flex flex-col gap-2.5">
                                  {previewComments.map((reply) => (
                                    <div key={reply.id} className="flex gap-2">
                                      <Link
                                        to={`/user/${reply.author.username}`}
                                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                                        style={{ background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)` }}
                                      >
                                        {reply.author.profilePicture ? (
                                          <img src={resolveAvatar(reply.author.profilePicture) || reply.author.profilePicture} alt={reply.author.username} className="w-full h-full rounded-full object-cover" />
                                        ) : (
                                          <PixelatedTurtle size={12} color={localTheme.turtleColor} />
                                        )}
                                      </Link>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                          <span className="text-xs font-semibold truncate" style={{ color: localTheme.textColor }}>
                                            {reply.author.profileName || reply.author.username}
                                          </span>
                                          <span className="text-xs shrink-0" style={{ color: localTheme.textColor + '66' }}>
                                            {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                          </span>
                                        </div>
                                        <p className="text-xs line-clamp-2" style={{ color: localTheme.textColor + 'cc' }}>{reply.content}</p>
                                        <div className="flex items-center gap-3 mt-1">
                                          <button
                                            onClick={(e) => { e.stopPropagation(); likeComment(reply.id, post.id); }}
                                            className="flex items-center gap-1"
                                            style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '66' }}
                                          >
                                            <Heart size={11} fill={reply.liked ? '#ef4444' : 'none'} />
                                            <span className="text-xs">{reply.likes}</span>
                                          </button>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); dislikeComment(reply.id, post.id); }}
                                            className="flex items-center gap-1"
                                            style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '66' }}
                                          >
                                            <ThumbsDown size={11} fill={reply.disliked ? '#f97316' : 'none'} />
                                            <span className="text-xs">{reply.dislikes}</span>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {post.comments > 3 && (
                                  <button
                                    onClick={() => navigate(`/posts/${post.id}`)}
                                    className="text-xs font-semibold mt-2 block"
                                    style={{ color: localTheme.accentColor }}
                                  >
                                    View all {post.comments} comments
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      ));
                    })()}
                    </div>
                  )}

                  {/* Media Tab */}
                  {activeTab === 'media' && (
                    <div className="w-full" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
                      {(() => {
                        const mediaPosts = posts.filter(post => post.mediaUrl || (post as any).imageUrl);
                        if (mediaPosts.length === 0) {
                          return (
                            <div className="rounded-xl p-6" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
                              <p className="text-center opacity-60" style={{ color: localTheme.textColor }}>No media posts yet</p>
                            </div>
                          );
                        }
                        return mediaPosts.map((post) => (
                          <div key={post.id} className="rounded-xl relative" style={{ background: getCardBackground(localTheme, customization, theme.cardBg), padding: '1rem', marginBottom: '0.5rem' }}>
                            {post.mediaUrl && (
                              <div className="mb-3 rounded-lg overflow-hidden">
                                <img src={post.mediaUrl} alt="Post media" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} />
                              </div>
                            )}
                            <p className="leading-relaxed opacity-90 text-sm" style={{ color: localTheme.textColor }}>{post.content}</p>
                            <div className="flex items-center gap-4 pt-3 mt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              <button onClick={() => handleLikePost(post.id)} className="flex items-center gap-1" style={{ color: (post as any).liked ? '#ef4444' : `${localTheme.textColor}99` }}>
                                <Heart size={16} fill={(post as any).liked ? '#ef4444' : 'none'} />
                                <span className="text-xs">{post.likes}</span>
                              </button>
                              <button onClick={() => navigate(`/posts/${post.id}`)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                <MessageSquare size={16} />
                                <span className="text-xs">{post.comments}</span>
                              </button>
                              <button onClick={() => handleShare(post.id)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                <Share2 size={16} />
                              </button>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}

                  {/* Community Tab */}
                  {activeTab === 'community' && (
                    <div className="w-full" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
                      {communityPosts.length === 0 ? (
                        <div className="rounded-xl p-6" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
                          <p className="text-center opacity-60" style={{ color: localTheme.textColor }}>No community posts yet</p>
                        </div>
                      ) : (
                        communityPosts.map((post) => (
                          <div key={post.id} className="rounded-xl relative" style={{ background: getCardBackground(localTheme, customization, theme.cardBg), padding: '1rem', marginBottom: '0.5rem' }}>
                            <Link
                              to={post.slug && post.communitySlug ? (post.postType === 'forum' ? getForumPostUrl(post.communitySlug, post.slug) : getCommunityPostUrl(post.communitySlug, post.slug)) : `/posts/${post.id}`}
                              className="hover:opacity-80 transition-opacity cursor-pointer"
                            >
                              <h2 className="text-lg font-bold" style={{ color: localTheme.textColor }}>{post.title || 'Community Post'}</h2>
                              <p className="text-xs opacity-60 mt-1 mb-2" style={{ color: localTheme.textColor }}>in {post.communityName}</p>
                              <p className="leading-relaxed opacity-90 text-sm" style={{ color: localTheme.textColor }}>{post.content}</p>
                            </Link>
                            <div className="flex items-center gap-4 pt-3 mt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              <span className="flex items-center gap-1 text-xs" style={{ color: `${localTheme.textColor}99` }}>
                                <Heart size={16} /> {post.likes}
                              </span>
                              <span className="flex items-center gap-1 text-xs" style={{ color: `${localTheme.textColor}99` }}>
                                <MessageSquare size={16} /> {post.comments}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Replies Tab */}
                  {activeTab === 'replies' && (
                    <div className="w-full" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
                      {replies.length === 0 ? (
                        <div className="rounded-xl p-6" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
                          <p className="text-center opacity-60" style={{ color: localTheme.textColor }}>No replies yet</p>
                        </div>
                      ) : (
                        replies.map((reply) => {
                          const postUrl = reply.type === 'legacy'
                            ? getLegacyPostUrl(reply.post.author.username, reply.post.slug || String(reply.post.id))
                            : getCommunityPostUrl(reply.post.communitySlug, reply.post.slug || String(reply.post.id));
                          return (
                            <div key={reply.id} className="rounded-xl p-4" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
                              <p className="text-xs opacity-70 mb-2" style={{ color: localTheme.textColor }}>
                                replied to @{reply.post.author.username}
                                {reply.post.title && <span> -- {reply.post.title}</span>}
                              </p>
                              <p className="text-sm mb-3" style={{ color: localTheme.textColor }}>{reply.content}</p>
                              <div
                                className="rounded-lg p-3 border-l-4 cursor-pointer hover:opacity-80 transition-opacity"
                                style={{ background: theme.cardBg.replace(/[\d.]+\)$/, '0.6)'), borderColor: localTheme.accentColor }}
                                onClick={() => navigate(postUrl)}
                              >
                                <p className="text-xs opacity-80 line-clamp-2" style={{ color: localTheme.textColor }}>{reply.post.content}</p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            <div key="right" className="pt-6 px-4 h-full overflow-auto flex flex-col gap-3">
              {/* Theme Customizer Button */}
              {user.isCurrentUser && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    requestAnimationFrame(() => setShowCustomizer(true));
                  }}
                  className="rounded-lg font-semibold w-full flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: localTheme.accentColor,
                    color: localTheme.textColor,
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    minHeight: '48px',
                  }}
                >
                  Customize Theme
                </button>
              )}
              {/* Moderation button */}
              {(currentUser && ((currentUser as any).isSiteModerator || (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || isAdmin)) && !user.isCurrentUser && (
                <button
                  onClick={handleToggleShowModerationModal}
                  className="rounded-lg shadow-lg font-semibold overflow-hidden flex items-center gap-2 w-full justify-center"
                  style={{
                    backgroundColor: localTheme.accentColor,
                    color: localTheme.textColor,
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    minHeight: '48px',
                  }}
                >
                  <Shield size={20} />
                  <span>Moderate</span>
                </button>
              )}
            </div>,
          ]}
        </SubPanelSwiper>
        {renderModals()}
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden" style={{ background: getBackground(localTheme, customization, theme.appBg || '#0891b2'), minHeight: backgroundHeight }}>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        @keyframes swim {
          0%, 100% { transform: translateX(0) translateY(0) scaleX(1); }
          25% { transform: translateX(30px) translateY(-10px) scaleX(1); }
          50% { transform: translateX(0) translateY(-20px) scaleX(-1); }
          75% { transform: translateX(-30px) translateY(-10px) scaleX(-1); }
        }
        @keyframes bubble {
          0% { transform: translateY(0) translateX(0); opacity: 0.8; }
          100% { transform: translateY(-100vh) translateX(20px); opacity: 0; }
        }
        .pixelated {
          image-rendering: pixelated;
          image-rendering: -moz-crisp-edges;
          image-rendering: crisp-edges;
        }
        .swipe-panel {
          transition: all 0.3s ease-in-out;
        }
      `}</style>

      {/* NEW: Animation Layer replaces old bubble/turtle code */}
      <AnimationLayer
        animations={animations}
        isMobile={isMobile}
        bubbleColor={localTheme.bubbleColor}
        enabled={animationsEnabled}
      />

      {/* Top Navigation Bar */}
      <MainNavbar
        key={`navbar-${localTheme.navbarBg}`}
        currentPath="/u"
        onLogout={handleLogout}
        onSignIn={() => setShowLoginModal(true)}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        theme={localTheme}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
        getNotificationIcon={getNotificationIcon}
        getNotificationMessage={getNotificationMessage}
        formatNotificationTime={formatNotificationTime}
        setNotifications={setNotifications}
        setUnreadCount={setUnreadCount}
      />

      {/* Main Content */}
      <div ref={swipeRef} className={`relative z-10 w-full overflow-hidden ${isMobile ? 'p-2 pt-6' : 'p-4 pt-8'}`}>
        <div
          className="transition-all duration-300"
          style={{
            opacity: currentSection === mapCardPositionToSection(profileCustomization.cardPosition) ? 1 : 0,
            pointerEvents: currentSection === mapCardPositionToSection(profileCustomization.cardPosition) ? 'auto' : 'none'
          }}
        >
        {/* Profile Header with Card Position */}
        <div className="flex w-full" style={{
          justifyContent: profileCustomization.cardPosition === 'center' ? 'center' : profileCustomization.cardPosition === 'right' ? 'flex-end' : 'flex-start'
        }}>
        <div className="rounded-2xl relative" style={{
          background: getCardBackground(localTheme, customization, theme.cardBg),
          width: isMobile ? '100%' : '50vw', // 50% of viewport width for desktop
          aspectRatio: isMobile ? 'auto' : '88/48', // Pokemon card ratio adjusted to 48mm height (sideways: 88mm wide x 48mm tall)
          padding: isMobile ? `${scaleH(16, scale.h)}px ${scaleW(16, scale.w)}px` : `${scaleH(24, scale.h)}px ${scaleW(24, scale.w)}px`,
          marginBottom: isMobile ? `${scaleH(24, scale.h)}px` : `${scaleH(40, scale.h)}px`
        }}>
          <div className={`flex ${isMobile ? 'flex-col' : ''}`} style={{
            paddingLeft: isMobile ? '0' : (profileCustomization.profilePicturePosition === 'right' ? `${scaleW(32, scale.w)}px` : `${scaleW(16, scale.w)}px`),
            paddingRight: isMobile ? '0' : (profileCustomization.profilePicturePosition === 'right' ? `${scaleW(16, scale.w)}px` : '0'),
            flexDirection: isMobile ? 'column' : (profileCustomization.profilePicturePosition === 'right' ? 'row-reverse' : 'row'),
            alignItems: isMobile ? 'flex-start' : 'flex-start',
            gap: isMobile ? `${scaleH(16, scale.h)}px` : `${scaleW(24, scale.w)}px`
          }}>
            {/* Profile Picture with Frame */}
            <div className="relative" style={{
              paddingTop: isMobile ? '0' : `${scaleH(16, scale.h)}px`
            }}>
              <div className="relative">
                <div
                  className="relative overflow-hidden pixelated"
                  style={{
                    width: isMobile ? `${scaleW(96, scale.w)}px` :
                      profileCustomization.profilePictureSize === 'large' ? `${scaleW(192, scale.w)}px` :
                      profileCustomization.profilePictureSize === 'medium' ? `${scaleW(160, scale.w)}px` :
                      `${scaleW(128, scale.w)}px`,
                    height: isMobile ? `${scaleW(96, scale.w)}px` :
                      profileCustomization.profilePictureSize === 'large' ? `${scaleW(192, scale.w)}px` :
                      profileCustomization.profilePictureSize === 'medium' ? `${scaleW(160, scale.w)}px` :
                      `${scaleW(128, scale.w)}px`,
                    border: `${scaleW(4, scale.w)}px solid`,
                    borderColor: localTheme.profileFrameColor,
                    borderRadius: profileCustomization.profileFrameShape === 'circle' ? '50%' : `${scaleW(12, scale.w)}px`
                  }}
                >
                  {profilePic ? (
                    <img
                      src={profilePic.startsWith('/api/') ? profilePic : getMediaUrl(profilePic)}
                      alt="Profile"
                      className="w-full h-full object-cover pixelated"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: theme.cardBg }}
                    >
                      <PixelatedTurtle key={`turtle-${localTheme.turtleColor}`} size={Math.round(isMobile ? scaleW(40, scale.w) : scaleW(60, scale.w))} color={localTheme.turtleColor} />
                    </div>
                  )}
                </div>

                {/* overlay decoration */}
                {ProfileFrameDesign}
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1" style={{
              position: 'relative',
              paddingTop: isMobile ? '0' : `${scaleH(16, scale.h)}px`,
              minHeight: `${scaleH(160, scale.h)}px`
            }}>
              {/* Name */}
              <div style={{
                position: isMobile ? 'relative' : 'absolute',
                top: isMobile ? 0 : `${scaleH(16, scale.h)}px`,
                // When centered, expand across entire card
                ...(profileCustomization.profileNameAlign === 'center' && !isMobile ? {
                  left: profileCustomization.profilePicturePosition === 'left' ? '-8rem' : '-10rem',
                  right: profileCustomization.profilePicturePosition === 'left' ? '-2rem' : '0rem',
                  width: profileCustomization.profilePicturePosition === 'left' ? 'calc(100% + 10rem)' : 'calc(100% + 10rem)'
                } : profileCustomization.profileNameAlign === 'right' && !isMobile ? {
                  // When right aligned, expand to left to accommodate follow button
                  left: profileCustomization.profilePicturePosition === 'left' ? '-8rem' : '-10rem',
                  right: 0,
                  width: profileCustomization.profilePicturePosition === 'left' ? 'calc(100% + 8rem)' : 'calc(100% + 10rem)'
                } : {
                  left: 0,
                  right: 0,
                  width: '100%'
                })
              }}>
              <div className="flex items-center" style={{
                justifyContent: profileCustomization.profileNameAlign === 'center' ? 'center' : profileCustomization.profileNameAlign === 'right' ? 'flex-end' : 'flex-start',
                flexDirection: profileCustomization.profileNameAlign === 'right' && !user.isCurrentUser ? 'row-reverse' : 'row',
                paddingRight: profileCustomization.profileNameAlign === 'right' ? `${scaleW(24, scale.w)}px` : '0',
                marginBottom: `${scaleH(8, scale.h)}px`,
                gap: `${scaleW(12, scale.w)}px`
              }}>
              <h1
              className="font-bold"
              style={{
                color: localTheme.textColor,
                fontSize: isMobile ? `${scaleW(24, scale.w)}px` : `${scaleW(30, scale.w)}px`
              }}
              >
              {profileName}
              </h1>
              {!user.isCurrentUser && (
                <button
                  onClick={async () => {
                    try {
                      const { followUser, unfollowUser } = await import('../../lib/api-client');
                      if (isFollowing) {
                        await unfollowUser(user.id);
                        setIsFollowing(false);
                      } else {
                        await followUser(user.id);
                        setIsFollowing(true);
                      }
                      // Emit event to refresh following widget
                      window.dispatchEvent(new CustomEvent('followingUpdated'));
                      // Trigger parent refetch to get updated follow status
                      onFollowChange?.();
                    } catch (error) {
                    }
                  }}
                  className="rounded-lg font-semibold transition-all hover:opacity-80"
                  style={{
                    paddingLeft: `${scaleW(20, scale.w)}px`,
                    paddingRight: `${scaleW(20, scale.w)}px`,
                    paddingTop: `${scaleH(8, scale.h)}px`,
                    paddingBottom: `${scaleH(8, scale.h)}px`,
                    fontSize: `${scaleW(15, scale.w)}px`,
                    minWidth: `${scaleW(112, scale.w)}px`,
                    backgroundColor: isFollowing ? 'rgba(255, 255, 255, 0.1)' : localTheme.accentColor,
                    color: localTheme.textColor,
                    border: `1px solid ${isFollowing ? localTheme.accentColor : 'transparent'}`,
                    opacity: isContentUpdating ? 0.5 : 1,
                    transition: 'opacity 0.15s ease'
                  }}
                >
                  {isContentUpdating ? 'Updating...' : (isMutualFollowers ? 'Friends' : isFollowing ? 'Following' : 'Follow')}
                </button>
              )}
              </div>
              </div>

              {/* Profile Title - larger than bio */}
              <div style={{
                position: isMobile ? 'relative' : 'absolute',
                top: isMobile ? 0 : `calc(${scaleH(56, scale.h)}px + 0.3rem)`,
                // When centered, expand across entire card
                ...(profileCustomization.profileTitleAlign === 'center' && !isMobile ? {
                  left: profileCustomization.profilePicturePosition === 'left' ? '-8rem' : '-10rem',
                  right: profileCustomization.profilePicturePosition === 'left' ? '-2rem' : '0rem',
                  width: profileCustomization.profilePicturePosition === 'left' ? 'calc(100% + 10rem)' : 'calc(100% + 10rem)'
                } : {
                  left: 0,
                  right: 0,
                  width: '100%'
                }),
                ...(profileCustomization.profileTitleAlign === 'right' && profileCustomization.profilePicturePosition === 'left' && !isMobile ? {
                  paddingRight: '2rem'
                } : {})
              }}>
              <p className="opacity-90" style={{
                color: localTheme.textColor,
                textAlign: profileCustomization.profileTitleAlign,
                fontSize: `${scaleW(18, scale.w)}px`,
                marginBottom: `${scaleH(12, scale.h)}px`
              }}>
              {profileTitle}
              </p>
              </div>

              {/* Profile Bio - the quotes, smaller text */}
              <div style={{
                position: isMobile ? 'relative' : 'absolute',
                top: isMobile ? 0 : `${scaleH(100, scale.h)}px`,
                // When centered, expand across entire card
                ...(profileCustomization.profileBioAlign === 'center' && !isMobile ? {
                  left: profileCustomization.profilePicturePosition === 'left' ? '-8rem' : '-10rem',
                  right: profileCustomization.profilePicturePosition === 'left' ? '-2rem' : '0rem',
                  width: profileCustomization.profilePicturePosition === 'left' ? 'calc(100% + 10rem)' : 'calc(100% + 10rem)'
                } : {
                  left: 0,
                  right: 0,
                  width: '100%'
                }),
                ...(profileCustomization.profileBioAlign === 'right' && profileCustomization.profilePicturePosition === 'left' && !isMobile ? {
                  paddingRight: '2rem'
                } : {})
              }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: `${scaleH(8, scale.h)}px` }}>
              {profileBio.split('\n').filter(line => line.trim()).map((line, i) => (
                <p key={i} className="italic opacity-85" style={{
                  color: localTheme.textColor,
                  textAlign: profileCustomization.profileBioAlign,
                  fontSize: `${scaleW(16, scale.w)}px`
                }}>
                {line}
                </p>
              ))}
              </div>
              </div>
            </div>
          </div>

          {/* Custom Header Design */}
          {headerDesign && (
            <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none relative">
              <img
                src={headerDesign}
                alt="Header design"
                className="absolute inset-0 w-full h-full object-contain"
              />
            </div>
          )}
        </div>
        </div>

        {/* Admin Post Creation */}
        {isAdmin && !user.isCurrentUser && (
          <div className="rounded-xl p-4 mb-6" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
            <div className="flex items-center gap-2 mb-3">
              <Shield size={20} style={{ color: localTheme.accentColor }} />
              <h3 className="font-semibold" style={{ color: localTheme.textColor }}>
                Admin Post
              </h3>
            </div>
            <div className="flex gap-3">
              <textarea
                value={newAdminPost}
                onChange={(e) => setNewAdminPost(e.target.value)}
                placeholder="Create an admin post for this user's profile..."
                className="flex-1 p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 resize-none"
                rows={3}
              />
              <button
                onClick={createAdminPost}
                disabled={!newAdminPost.trim()}
                className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                style={{ backgroundColor: localTheme.accentColor, color: localTheme.textColor }}
              >
                Post & Pin
              </button>
            </div>
          </div>
        )}

        {/* Tab Bar - Aligned with Card */}
        <div className="flex w-full" style={{
          justifyContent: profileCustomization.cardPosition === 'center' ? 'center' : profileCustomization.cardPosition === 'right' ? 'flex-end' : 'flex-start',
          marginTop: '-1.5rem',
          overflow: 'visible'
        }}>
          <div style={{ width: isMobile ? '100%' : '50vw', overflow: 'visible' }}>
            {/* Navigation Tabs and Sort Dropdown */}
            <div className={`flex items-center ${isMobile ? 'overflow-x-auto' : ''}`} style={{
              justifyContent: profileCustomization.tabAlignment === 'center' ? 'center' : profileCustomization.tabAlignment === 'right' ? 'flex-end' : 'flex-start',
              marginBottom: `${scaleH(32, scale.h)}px`,
              gap: `${scaleW(12, scale.w)}px`,
              position: 'relative',
              zIndex: 100,
              overflowY: 'visible'
            }}>
              {/* Tabs */}
              {['Personal', 'Media', 'Community', 'Replies'].map((tab, index) => (
                <button
                  key={tab}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    requestAnimationFrame(() => setActiveTab(tab.toLowerCase()));
                  }}
                  className="rounded-xl font-semibold transition-all pixelated whitespace-nowrap"
                  style={{
                    backgroundColor: activeTab === tab.toLowerCase()
                      ? localTheme.accentColor
                      : localTheme.tabBarBg,
                    color: localTheme.textColor,
                    border: `2px solid ${activeTab === tab.toLowerCase() ? localTheme.accentColor : 'transparent'}`,
                    paddingLeft: `${scaleW(24, scale.w)}px`,
                    paddingRight: `${scaleW(24, scale.w)}px`,
                    paddingTop: `${scaleH(12, scale.h)}px`,
                    paddingBottom: `${scaleH(12, scale.h)}px`,
                    fontSize: isMobile ? `${scaleW(14, scale.w)}px` : `${scaleW(20, scale.w)}px`,
                  }}
                >
                  {tab}
                </button>
              ))}

              {/* Sort Dropdown */}
              <div className="relative" style={{ zIndex: 41 }}>
                <button
                  onClick={handleToggleShowSortMenu}
                  className="flex items-center rounded-lg font-medium transition-all"
                  style={{
                    backgroundColor: getDropdownButtonBg(),
                    border: 'none',
                    color: localTheme.textColor,
                    gap: `${scaleW(8, scale.w)}px`,
                    paddingLeft: `${scaleW(16, scale.w)}px`,
                    paddingRight: `${scaleW(16, scale.w)}px`,
                    paddingTop: `${scaleH(8, scale.h)}px`,
                    paddingBottom: `${scaleH(8, scale.h)}px`,
                    fontSize: `${scaleW(14, scale.w)}px`
                  }}
                >
                  <currentSortOption.icon size={Math.round(scaleW(16, scale.w))} />
                  <span>{currentSortOption.label}</span>
                  <ChevronDown size={Math.round(scaleW(16, scale.w))} className={showSortMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
                </button>

                {showSortMenu && (
                  <>
                    <div
                      className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl"
                      style={{
                        backgroundColor: getDropdownMenuBg(),
                        borderColor: localTheme.textColor,
                        border: '2px solid',
                        zIndex: 42,
                        marginTop: `${scaleH(8, scale.h)}px`,
                        minWidth: `${scaleW(160, scale.w)}px`
                      }}
                    >
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortMenu(false);
                          }}
                          className="flex items-center w-full text-left font-medium transition-all"
                          style={{
                            color: option.value === sortBy ? localTheme.textColor : localTheme.textColor,
                            backgroundColor: option.value === sortBy ? localTheme.highlightColor : 'transparent',
                            gap: `${scaleW(8, scale.w)}px`,
                            paddingLeft: `${scaleW(16, scale.w)}px`,
                            paddingRight: `${scaleW(16, scale.w)}px`,
                            paddingTop: `${scaleH(12, scale.h)}px`,
                            paddingBottom: `${scaleH(12, scale.h)}px`,
                            fontSize: `${scaleW(14, scale.w)}px`
                          }}
                          onMouseEnter={(e) => {
                            if (option.value !== sortBy) {
                              e.currentTarget.style.backgroundColor = localTheme.highlightColor + '40';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (option.value !== sortBy) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <option.icon size={Math.round(scaleW(16, scale.w))} />
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <div
                      className="fixed inset-0"
                      style={{ zIndex: 9998 }}
                      onClick={handleCloseShowSortMenu}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Posts - Aligned independently based on postsAlignment setting */}
        <div className="w-full" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: profileCustomization.postsAlignment === 'center' ? 'center' : profileCustomization.postsAlignment === 'right' ? 'flex-end' : 'flex-start',
          paddingLeft: isMobile ? `${scaleW(8, scale.w)}px` : 0,
          paddingRight: isMobile ? `${scaleW(8, scale.w)}px` : 0,
          // Keep room below the tab bar for the sort dropdown even when there are no posts
          // (the page root is overflow-hidden and only as tall as its content).
          minHeight: `${sortOptions.length * 56 + 96}px`
        }}>
          <div style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '50vw',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch'
          }}>
            {/* Posts - Personal Tab */}
            {activeTab === 'personal' && (
              <div className="w-full" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '2rem'
              }}>
            {(() => {
              // Apply sorting to personal posts
              let sortedPosts = [...posts];
              switch(sortBy) {
                case 'newest':
                  sortedPosts = sortedPosts.sort((a, b) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                  );
                  break;
                case 'oldest':
                  sortedPosts = sortedPosts.sort((a, b) =>
                    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                  );
                  break;
                case 'popular':
                  sortedPosts = sortedPosts.sort((a, b) => b.likes - a.likes);
                  break;
                case 'unpopular':
                  sortedPosts = sortedPosts.sort((a, b) =>
                    (b.dislikes || 0) - (a.dislikes || 0)
                  );
                  break;
                case 'controversial':
                  sortedPosts = sortedPosts.sort((a, b) => {
                    const aControversy = Math.min(a.likes, a.dislikes || 0);
                    const bControversy = Math.min(b.likes, b.dislikes || 0);
                    return bControversy - aControversy;
                  });
                  break;
                default:
                  break;
              }

              return sortedPosts.map((post, index) => {
              const postStyle = getPostStyle(index);
              return (
              <div
                key={post.id}
                ref={(el) => {
                  if (el) postRefs.current.set(post.id, el);
                  else postRefs.current.delete(post.id);
                }}
                data-post-id={post.id}
                className="rounded-xl relative"
                style={{
                  background: getCardBackground(localTheme, customization, theme.cardBg),
                  padding: isMobile ? `${scaleH(16, scale.h)}px ${scaleW(12, scale.w)}px` : '1.5rem',
                  ...postStyle,
                  marginBottom: isMobile ? `${scaleH(16, scale.h)}px` : '2rem',
                }}>
                {/* Post Badges */}
                <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

                {/* Author Header - Only show for OTHER users' posts */}
                {!user.isCurrentUser && (post as any).author && (
                  <div className="flex items-center gap-3 mb-4">
                    <Link to={`/profile/${(post as any).authorUsername || (post as any).authorId}`}>
                      <div
                        className="rounded-full overflow-hidden"
                        style={{
                          width: '40px',
                          height: '40px',
                          border: `2px solid ${localTheme.accentColor}`
                        }}
                      >
                        <img
                          src={(post as any).authorAvatar || '/default-avatar.png'}
                          alt={(post as any).author}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>
                    <div className="flex-1">
                      <Link to={`/profile/${(post as any).authorUsername || (post as any).authorId}`}>
                        <p className="font-semibold" style={{ color: localTheme.textColor }}>
                          {(post as any).author}
                        </p>
                        <p className="text-sm opacity-60" style={{ color: localTheme.textColor }}>
                          @{(post as any).authorUsername || 'user'}
                        </p>
                      </Link>
                    </div>
                    <div className="text-sm opacity-60" style={{ color: localTheme.textColor }}>
                      {new Date(post.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      })}, {new Date(post.createdAt).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      })}
                    </div>
                  </div>
                )}

                {/* Timestamp - Only for profile user's own posts */}
                {user.isCurrentUser && (
                  <div className="flex justify-end" style={{ marginBottom: '1rem', marginTop: '-1rem', marginRight: '0.5rem' }}>
                    <div className="text-sm opacity-60" style={{ color: localTheme.textColor }}>
                      {new Date(post.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      })}, {new Date(post.createdAt).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      })}
                    </div>
                  </div>
                )}

                {/* Moderate Dropdown */}
                {currentUser && canModeratePost(post as any, {
                  id: parseInt(currentUser.id),
                  username: currentUser.username,
                  isAdmin: isAdmin || false,
                  isModerator: (currentUser as any).isSiteModerator || false
                }) && (
                <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                  <button
                    onClick={() => setShowModerateMenu(showModerateMenu === post.id ? null : post.id)}
                    className="p-1.5 rounded-lg transition-all hover:opacity-80"
                    style={{
                      backgroundColor: localTheme.accentColor + '20',
                      color: localTheme.textColor
                    }}
                  >
                    <MoreVertical size={16} />
                  </button>

                  {showModerateMenu === post.id && (
                    <>
                      <div
                        className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                        style={{
                          backgroundColor: theme.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                          borderColor: localTheme.accentColor + '60',
                          border: '1px solid',
                          zIndex: 1001
                        }}
                      >
                        <button
                          onClick={() => handleEditPost(post as any)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: localTheme.textColor,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Edit3 size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => handlePinPost(post as any)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: localTheme.textColor,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Pin size={16} />
                          {(post as any).isPinned ? 'Unpin' : 'Pin'}
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handlePinToFeed(post as any)}
                            className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                            style={{ color: localTheme.textColor, backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Pin size={16} />
                            {(post as any).isPinnedToFeed ? 'Unpin Feed Pin' : 'Feed Pin'}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePost(post as any)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: '#ef4444',
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef444420';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 90 }}
                        onClick={handleCloseShowModerateMenu}
                      />
                    </>
                  )}
                </div>
                )}
                {post.title && (
                  <div
                    className="flex justify-between items-start mb-4"
                    style={{ marginTop: user.isCurrentUser ? '-2.5rem' : '0' }}
                  >
                    <Link
                      to={post.slug && user.username ? getLegacyPostUrl(user.username, post.slug) : `/posts/${post.id}`}
                      state={{
                        from: 'profile',
                        authorId: user.id,
                        themeContext: {
                          type: 'profile',
                          userId: user.id,
                          colors: {
                            primaryColor: localTheme.primaryColor,
                            secondaryColor: localTheme.secondaryColor,
                            textColor: localTheme.textColor,
                            accentColor: localTheme.accentColor
                          }
                        }
                      }}
                      className="hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <h2 className="text-2xl font-bold" style={{ color: localTheme.textColor }}>
                        {post.title}
                      </h2>
                    </Link>
                    <div className="flex gap-2">
                      {post.pinned && (
                        <Pin size={20} style={{ color: '#ef4444' }} />
                      )}
                    </div>
                  </div>
                )}

                {/* Media above content */}
                {post.aboveContent && post.mediaUrl && (
                  <div className="mb-3 rounded-lg overflow-hidden">
                    <img
                      src={post.mediaUrl}
                      alt="Post media"
                      className="w-full h-auto"
                      style={{ maxHeight: '400px', objectFit: 'cover' }}
                    />
                  </div>
                )}

                {isEditing === post.id ? (
                  <textarea
                    value={post.content}
                    onChange={(e) => editPost(post.id, e.target.value)}
                    className="w-full leading-relaxed opacity-90 bg-transparent border p-2 rounded"
                    style={{ color: localTheme.textColor, borderColor: localTheme.accentColor }}
                    rows={4}
                  />
                ) : (
                  <Link
                    to={post.slug && user.username ? getLegacyPostUrl(user.username, post.slug) : `/posts/${post.id}`}
                    state={{
                      from: 'profile',
                      authorId: user.id,
                      themeContext: {
                        type: 'profile',
                        userId: user.id,
                        colors: {
                          primaryColor: localTheme.primaryColor,
                          secondaryColor: localTheme.secondaryColor,
                          textColor: localTheme.textColor,
                          accentColor: localTheme.accentColor
                        }
                      }
                    }}
                    className="block hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    <p className="leading-relaxed opacity-90" style={{ color: localTheme.textColor }}>
                      {post.content}
                    </p>
                  </Link>
                )}

                {/* Media below content */}
                {post.belowContent && post.mediaUrl && (
                  <div className="mt-3 rounded-lg overflow-hidden">
                    <img
                      src={post.mediaUrl}
                      alt="Post media"
                      className="w-full h-auto"
                      style={{ maxHeight: '400px', objectFit: 'cover' }}
                    />
                  </div>
                )}
                
                {post.edited && (
                  <p className="text-sm opacity-60 mt-2 text-right" style={{ color: localTheme.textColor }}>
                    (edited)
                  </p>
                )}

                {/* Post Actions */}
                <div className="flex items-center gap-6 pt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', marginTop: '1rem' }}>
                  <button
                    onClick={() => handleLikePost(post.id)}
                    className="flex items-center gap-2 transition-colors hover:text-red-400"
                    style={{ color: (post as any).liked ? '#ef4444' : `${localTheme.textColor}99` }}
                  >
                    <Heart size={18} fill={(post as any).liked ? '#ef4444' : 'none'} />
                    <span className="text-sm">{post.likes}</span>
                  </button>
                  <button
                    onClick={() => handleDislikePost(post.id)}
                    className="flex items-center gap-2 transition-colors hover:text-orange-400"
                    style={{ color: (post as any).disliked ? '#f97316' : `${localTheme.textColor}99` }}
                  >
                    <ThumbsDown size={18} fill={(post as any).disliked ? '#f97316' : 'none'} />
                    <span className="text-sm">{post.dislikes}</span>
                  </button>
                  <button
                    onClick={() => navigate(`/posts/${post.id}`, {
                      state: {
                        from: 'profile',
                        themeContext: {
                          type: 'profile',
                          userId: user.id,
                          colors: {
                            primaryColor: localTheme.primaryColor,
                            secondaryColor: localTheme.secondaryColor,
                            textColor: localTheme.textColor,
                            accentColor: localTheme.accentColor
                          }
                        }
                      }
                    })}
                    className="flex items-center gap-2 transition-colors"
                    style={{ color: `${localTheme.textColor}99` }}
                  >
                    <MessageSquare size={18} />
                    <span className="text-sm">{post.comments}</span>
                  </button>
                  <button
                    onClick={() => handleRepost(post.id)}
                    className="flex items-center gap-2 transition-colors hover:text-green-400"
                    style={{ color: `${localTheme.textColor}99` }}
                  >
                    <Repeat2 size={18} />
                    <span className="text-sm">{post.reposts || 0}</span>
                  </button>
                  <button
                    onClick={() => handleShare(post.id)}
                    className="flex items-center gap-2 transition-colors hover:text-blue-400"
                    style={{ color: `${localTheme.textColor}99` }}
                  >
                    <Share2 size={18} />
                  </button>
                </div>

                {/* Comment Preview - Max 3 comments */}
                {(post as any).replies && (post as any).replies.length > 0 && (() => {
                  const previewComments = selectPreviewComments((post as any).replies);
                  return (
                    <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                      <div className="space-y-3">
                        {previewComments.map((reply) => (
                          <div key={reply.id} className="flex gap-3">
                            <Link
                              to={`/user/${reply.author.username}`}
                              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{
                                background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                              }}
                            >
                              {reply.author.profilePicture ? (
                                <img
                                  src={resolveAvatar(reply.author.profilePicture) || reply.author.profilePicture}
                                  alt={reply.author.username}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                <PixelatedTurtle key={`turtle-comment-${localTheme.turtleColor}`} size={16} color={localTheme.turtleColor} />
                              )}
                            </Link>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Link
                                  to={`/user/${reply.author.username}`}
                                  className="text-xs font-semibold hover:opacity-80"
                                  style={{ color: localTheme.textColor }}
                                >
                                  {reply.author.profileName || reply.author.username}
                                </Link>
                                <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>
                                  {new Date(reply.createdAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                              </div>
                              <p className="text-xs" style={{ color: localTheme.textColor }}>{reply.content}</p>
                              <div className="flex items-center gap-3 mt-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    likeComment(reply.id, post.id);
                                  }}
                                  className="flex items-center gap-1 transition-colors hover:opacity-80"
                                  style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '99' }}
                                >
                                  <Heart size={12} fill={reply.liked ? '#ef4444' : 'none'} />
                                  <span className="text-xs">{reply.likes}</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dislikeComment(reply.id, post.id);
                                  }}
                                  className="flex items-center gap-1 transition-colors hover:opacity-80"
                                  style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '99' }}
                                >
                                  <ThumbsDown size={12} fill={reply.disliked ? '#f97316' : 'none'} />
                                  <span className="text-xs">{reply.dislikes}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {post.comments > 3 && (
                        <div className="mt-2">
                          <button
                            onClick={() => navigate(`/posts/${post.id}`, {
                              state: {
                                from: 'profile',
                                themeContext: {
                                  type: 'profile',
                                  userId: user.id,
                                  colors: {
                                    primaryColor: localTheme.primaryColor,
                                    secondaryColor: localTheme.secondaryColor,
                                    textColor: localTheme.textColor,
                                    accentColor: localTheme.accentColor
                                  }
                                }
                              }
                            })}
                            className="text-xs font-semibold hover:opacity-80 transition-opacity"
                            style={{ color: localTheme.accentColor }}
                          >
                            View all {post.comments} comments
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
            });
            })()}
          </div>
        )}

        {/* Other Tabs */}
        {activeTab === 'media' && (
          <div className="w-full" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: '2rem'
          }}>
            {(() => {
              const mediaPosts = posts.filter(post => post.mediaUrl || (post as any).imageUrl);

              if (mediaPosts.length === 0) {
                return (
                  <div className="rounded-xl p-6" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
                    <p className="text-center opacity-60" style={{ color: localTheme.textColor }}>
                      No media posts yet
                    </p>
                  </div>
                );
              }

              // Apply sorting to media posts
              let sortedPosts: any[] = [...mediaPosts];
              switch(sortBy) {
                case 'newest':
                  sortedPosts = sortedPosts.sort((a, b) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                  );
                  break;
                case 'oldest':
                  sortedPosts = sortedPosts.sort((a, b) =>
                    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                  );
                  break;
                case 'popular':
                  sortedPosts = sortedPosts.sort((a, b) => b.likes - a.likes);
                  break;
                case 'unpopular':
                  sortedPosts = sortedPosts.sort((a, b) =>
                    (b.dislikes || 0) - (a.dislikes || 0)
                  );
                  break;
                case 'controversial':
                  sortedPosts = sortedPosts.sort((a, b) => {
                    const aControversy = Math.min(a.likes, a.dislikes || 0);
                    const bControversy = Math.min(b.likes, b.dislikes || 0);
                    return bControversy - aControversy;
                  });
                  break;
                default:
                  break;
              }

              return sortedPosts.map((post, index) => {
                const postStyle = getPostStyle(index);
                return (
                  <div
                    key={post.id}
                    ref={(el) => {
                      if (el) postRefs.current.set(post.id, el);
                      else postRefs.current.delete(post.id);
                    }}
                    data-post-id={post.id}
                    className="rounded-xl relative"
                    style={{
                      background: getCardBackground(localTheme, customization, theme.cardBg),
                      padding: isMobile ? `${scaleH(16, scale.h)}px ${scaleW(12, scale.w)}px` : '1.5rem',
                      ...postStyle,
                      marginBottom: isMobile ? `${scaleH(16, scale.h)}px` : '2rem',
                    }}>
                    {/* Post Badges */}
                    <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

                    {/* Moderate Dropdown */}
                    {currentUser && canModeratePost(post as any, {
                      id: parseInt(currentUser.id),
                      username: currentUser.username,
                      isAdmin: isAdmin || false,
                      isModerator: (currentUser as any).isSiteModerator || false
                    }) && (
                    <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                      <button
                        onClick={() => setShowModerateMenu(showModerateMenu === post.id ? null : post.id)}
                        className="p-1.5 rounded-lg transition-all hover:opacity-80"
                        style={{
                          backgroundColor: localTheme.accentColor + '20',
                          color: localTheme.textColor
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>

                      {showModerateMenu === post.id && (
                        <>
                          <div
                            className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                            style={{
                              backgroundColor: theme.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                              borderColor: localTheme.accentColor + '60',
                              border: '1px solid',
                              zIndex: 1001
                            }}
                          >
                            <button
                              onClick={() => handleEditPost(post as any)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Edit3 size={16} />
                              Edit
                            </button>
                            <button
                              onClick={() => handlePinPost(post as any)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Pin size={16} />
                              {(post as any).isPinned ? 'Unpin' : 'Pin'}
                            </button>
                            {isSuperAdmin && (
                              <button
                                onClick={() => handlePinToFeed(post as any)}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{ color: localTheme.textColor, backgroundColor: 'transparent' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Pin size={16} />
                                {(post as any).isPinnedToFeed ? 'Unpin Feed Pin' : 'Feed Pin'}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeletePost(post as any)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: '#ef4444',
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#ef444420';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          </div>
                          <div
                            className="fixed inset-0"
                            style={{ zIndex: 90 }}
                            onClick={handleCloseShowModerateMenu}
                          />
                        </>
                      )}
                    </div>
                    )}
                    {post.title && (
                      <div className="flex justify-between items-start mb-4">
                        <Link
                          to={post.slug && user.username ? getLegacyPostUrl(user.username, post.slug) : `/posts/${post.id}`}
                          state={{
                            from: 'profile',
                            authorId: user.id,
                            themeContext: {
                              type: 'profile',
                              userId: user.id,
                              colors: {
                                primaryColor: localTheme.primaryColor,
                                secondaryColor: localTheme.secondaryColor,
                                textColor: localTheme.textColor,
                                accentColor: localTheme.accentColor
                              }
                            }
                          }}
                          className="hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          <h2 className="text-2xl font-bold" style={{ color: localTheme.textColor }}>
                            {post.title}
                          </h2>
                        </Link>
                        <div className="flex gap-2">
                          {post.pinned && (
                            <Pin size={20} style={{ color: '#ef4444' }} />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Media */}
                    {post.mediaUrl && (
                      <div className="mb-3 rounded-lg overflow-hidden">
                        <img
                          src={post.mediaUrl}
                          alt="Post media"
                          className="w-full h-auto cursor-context-menu"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                          onClick={(e) => e.preventDefault()}
                          onContextMenu={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}

                    {/* Post Content with Link */}
                    <Link
                      to={post.slug && user.username ? getLegacyPostUrl(user.username, post.slug) : `/posts/${post.id}`}
                      state={{
                        from: 'profile',
                        authorId: user.id,
                        themeContext: {
                          type: 'profile',
                          userId: user.id,
                          colors: {
                            primaryColor: localTheme.primaryColor,
                            secondaryColor: localTheme.secondaryColor,
                            textColor: localTheme.textColor,
                            accentColor: localTheme.accentColor
                          }
                        }
                      }}
                      className="block hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <p className="leading-relaxed opacity-90" style={{ color: localTheme.textColor }}>
                        {post.content}
                      </p>
                    </Link>

                    {/* Post Actions */}
                    <div className="flex items-center gap-6 mt-4 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <button
                        onClick={() => handleLikePost(post.id)}
                        className="flex items-center gap-2 transition-colors hover:text-red-400"
                        style={{ color: (post as any).liked ? '#ef4444' : `${localTheme.textColor}99` }}
                      >
                        <Heart size={18} fill={(post as any).liked ? '#ef4444' : 'none'} />
                        <span className="text-sm">{post.likes}</span>
                      </button>
                      <button
                        onClick={() => handleDislikePost(post.id)}
                        className="flex items-center gap-2 transition-colors hover:text-orange-400"
                        style={{ color: (post as any).disliked ? '#f97316' : `${localTheme.textColor}99` }}
                      >
                        <ThumbsDown size={18} fill={(post as any).disliked ? '#f97316' : 'none'} />
                        <span className="text-sm">{post.dislikes}</span>
                      </button>
                      <button
                        onClick={() => navigate(`/posts/${post.id}`, {
                          state: {
                            from: 'profile',
                            themeContext: {
                              type: 'profile',
                              userId: user.id,
                              colors: {
                                primaryColor: localTheme.primaryColor,
                                secondaryColor: localTheme.secondaryColor,
                                textColor: localTheme.textColor,
                                accentColor: localTheme.accentColor
                              }
                            }
                          }
                        })}
                        className="flex items-center gap-2 transition-colors"
                        style={{ color: `${localTheme.textColor}99` }}
                      >
                        <MessageSquare size={18} />
                        <span className="text-sm">{post.comments}</span>
                      </button>
                      <button
                        onClick={() => handleRepost(post.id)}
                        className="flex items-center gap-2 transition-colors hover:text-green-400"
                        style={{ color: `${localTheme.textColor}99` }}
                      >
                        <Repeat2 size={18} />
                        <span className="text-sm">{post.reposts || 0}</span>
                      </button>
                      <button
                        onClick={() => handleShare(post.id)}
                        className="flex items-center gap-2 transition-colors hover:text-blue-400"
                        style={{ color: `${localTheme.textColor}99` }}
                      >
                        <Share2 size={18} />
                      </button>
                    </div>

                    {/* Comment Preview - Max 3 comments */}
                    {(post as any).replies && (post as any).replies.length > 0 && (() => {
                      const previewComments = selectPreviewComments((post as any).replies);
                      return (
                        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                          <div className="space-y-3">
                            {previewComments.map((reply) => (
                              <div key={reply.id} className="flex gap-3">
                                <Link
                                  to={`/user/${reply.author.username}`}
                                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                  }}
                                >
                                  {reply.author.profilePicture ? (
                                    <img
                                      src={reply.author.profilePicture}
                                      alt={reply.author.username}
                                      className="w-full h-full rounded-full object-cover"
                                    />
                                  ) : (
                                    <PixelatedTurtle key={`turtle-comment-${localTheme.turtleColor}`} size={16} color={localTheme.turtleColor} />
                                  )}
                                </Link>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Link
                                      to={`/user/${reply.author.username}`}
                                      className="text-xs font-semibold hover:opacity-80"
                                      style={{ color: localTheme.textColor }}
                                    >
                                      {reply.author.profileName || reply.author.username}
                                    </Link>
                                    <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>
                                      {new Date(reply.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <p className="text-xs" style={{ color: localTheme.textColor }}>{reply.content}</p>
                                  <div className="flex items-center gap-3 mt-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        likeComment(reply.id, post.id);
                                      }}
                                      className="flex items-center gap-1 transition-colors hover:opacity-80"
                                      style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '99' }}
                                    >
                                      <Heart size={12} fill={reply.liked ? '#ef4444' : 'none'} />
                                      <span className="text-xs">{reply.likes}</span>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dislikeComment(reply.id, post.id);
                                      }}
                                      className="flex items-center gap-1 transition-colors hover:opacity-80"
                                      style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '99' }}
                                    >
                                      <ThumbsDown size={12} fill={reply.disliked ? '#f97316' : 'none'} />
                                      <span className="text-xs">{reply.dislikes}</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {post.comments > 3 && (
                            <div className="mt-2">
                              <button
                                onClick={() => navigate(`/posts/${post.id}`, {
                                  state: {
                                    from: 'profile',
                                    themeContext: {
                                      type: 'profile',
                                      userId: user.id,
                                      colors: {
                                        primaryColor: localTheme.primaryColor,
                                        secondaryColor: localTheme.secondaryColor,
                                        textColor: localTheme.textColor,
                                        accentColor: localTheme.accentColor
                                      }
                                    }
                                  }
                                })}
                                className="text-xs font-semibold hover:opacity-80 transition-opacity"
                                style={{ color: localTheme.accentColor }}
                              >
                                View all {post.comments} comments
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {activeTab === 'community' && (
          <div className="w-full" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: '2rem'
          }}>
            {communityPosts.length === 0 ? (
              <div className="rounded-xl p-6" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
                <p className="text-center opacity-60" style={{ color: localTheme.textColor }}>
                  No community posts yet
                </p>
              </div>
            ) : (
              (() => {
                // Apply sorting to community posts
                let sortedPosts: any[] = [...communityPosts];
                switch(sortBy) {
                  case 'newest':
                    sortedPosts = sortedPosts.sort((a, b) =>
                      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    );
                    break;
                  case 'oldest':
                    sortedPosts = sortedPosts.sort((a, b) =>
                      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                    );
                    break;
                  case 'popular':
                    sortedPosts = sortedPosts.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                    break;
                  case 'unpopular':
                    sortedPosts = sortedPosts.sort((a, b) =>
                      (b.dislikes || 0) - (a.dislikes || 0)
                    );
                    break;
                  case 'controversial':
                    sortedPosts = sortedPosts.sort((a, b) => {
                      const aControversy = Math.min(a.likes || 0, a.dislikes || 0);
                      const bControversy = Math.min(b.likes || 0, b.dislikes || 0);
                      return bControversy - aControversy;
                    });
                    break;
                  default:
                    break;
                }

                return sortedPosts.map((post, index) => {
                const postStyle = getPostStyle(index);
                return (
                  <div
                    key={post.id}
                    ref={(el) => {
                      if (el) postRefs.current.set(post.id, el);
                      else postRefs.current.delete(post.id);
                    }}
                    data-post-id={post.id}
                    className="rounded-xl relative"
                    style={{
                      background: getCardBackground(localTheme, customization, theme.cardBg),
                      padding: isMobile ? `${scaleH(16, scale.h)}px ${scaleW(12, scale.w)}px` : '1.5rem',
                      ...postStyle,
                      marginBottom: isMobile ? `${scaleH(16, scale.h)}px` : '2rem',
                    }}>
                    {/* Post Badges */}
                    <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

                    {/* Moderate Dropdown */}
                    {currentUser && canModeratePost(post as any, {
                      id: parseInt(currentUser.id),
                      username: currentUser.username,
                      isAdmin: isAdmin || false,
                      isModerator: (currentUser as any).isSiteModerator || false
                    }) && (
                    <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                      <button
                        onClick={() => setShowModerateMenu(showModerateMenu === post.id ? null : post.id)}
                        className="p-1.5 rounded-lg transition-all hover:opacity-80"
                        style={{
                          backgroundColor: localTheme.accentColor + '20',
                          color: localTheme.textColor
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>

                      {showModerateMenu === post.id && (
                        <>
                          <div
                            className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                            style={{
                              backgroundColor: theme.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                              borderColor: localTheme.accentColor + '60',
                              border: '1px solid',
                              zIndex: 1001
                            }}
                          >
                            <button
                              onClick={() => handleEditPost(post as any)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Edit3 size={16} />
                              Edit
                            </button>
                            <button
                              onClick={() => handlePinPost(post as any)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Pin size={16} />
                              {(post as any).isPinned ? 'Unpin' : 'Pin'}
                            </button>
                            {isSuperAdmin && (
                              <button
                                onClick={() => handlePinToFeed(post as any)}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{ color: localTheme.textColor, backgroundColor: 'transparent' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Pin size={16} />
                                {(post as any).isPinnedToFeed ? 'Unpin Feed Pin' : 'Feed Pin'}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeletePost(post as any)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: '#ef4444',
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#ef444420';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          </div>
                          <div
                            className="fixed inset-0"
                            style={{ zIndex: 90 }}
                            onClick={handleCloseShowModerateMenu}
                          />
                        </>
                      )}
                    </div>
                    )}
                    <div className="flex justify-between items-start mb-4">
                      <Link
                        to={
                          post.slug && post.communitySlug
                            ? (post.postType === 'forum'
                                ? getForumPostUrl(post.communitySlug, post.slug)
                                : getCommunityPostUrl(post.communitySlug, post.slug))
                            : `/posts/${post.id}`
                        }
                        className="hover:opacity-80 transition-opacity cursor-pointer"
                      >
                        <div>
                          <h2 className="text-2xl font-bold" style={{ color: localTheme.textColor }}>
                            {post.title || 'Community Post'}
                          </h2>
                          <p className="text-sm opacity-60 mt-1" style={{ color: localTheme.textColor }}>
                            in {post.communityName}
                          </p>
                        </div>
                      </Link>
                      <div className="flex gap-2">
                        {post.pinned && (
                          <Pin size={20} style={{ color: '#ef4444' }} />
                        )}
                      </div>
                    </div>

                    {/* Media */}
                    {post.imageUrl && (
                      <div className="mb-3 rounded-lg overflow-hidden">
                        <img
                          src={post.imageUrl}
                          alt="Post media"
                          className="w-full h-auto"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                        />
                      </div>
                    )}

                    {/* Post Content with Link */}
                    <Link
                      to={
                        post.slug && post.communitySlug
                          ? (post.postType === 'forum'
                              ? getForumPostUrl(post.communitySlug, post.slug)
                              : getCommunityPostUrl(post.communitySlug, post.slug))
                          : `/posts/${post.id}`
                      }
                      className="block hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <p className="leading-relaxed opacity-90" style={{ color: localTheme.textColor }}>
                        {post.content}
                      </p>
                    </Link>

                    {/* Post Actions */}
                    <div className="flex items-center gap-6 mt-4 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <button
                        className="flex items-center gap-2 transition-colors"
                        style={{ color: `${localTheme.textColor}99` }}
                      >
                        <Heart size={18} />
                        <span className="text-sm">{post.likes}</span>
                      </button>
                      <button
                        className="flex items-center gap-2 transition-colors"
                        style={{ color: `${localTheme.textColor}99` }}
                      >
                        <ThumbsDown size={18} />
                        <span className="text-sm">{post.dislikes}</span>
                      </button>
                      <button
                        onClick={() => navigate(
                          post.slug && post.communitySlug
                            ? (post.postType === 'forum'
                                ? getForumPostUrl(post.communitySlug, post.slug)
                                : getCommunityPostUrl(post.communitySlug, post.slug))
                            : `/posts/${post.id}`
                        )}
                        className="flex items-center gap-2 transition-colors"
                        style={{ color: `${localTheme.textColor}99` }}
                      >
                        <MessageSquare size={18} />
                        <span className="text-sm">{post.comments}</span>
                      </button>
                      <button
                        onClick={() => handleRepost(post.id)}
                        className="flex items-center gap-2 transition-colors hover:text-green-400"
                        style={{ color: `${localTheme.textColor}99` }}
                      >
                        <Repeat2 size={18} />
                      </button>
                      <button
                        onClick={() => handleShare(post.id)}
                        className="flex items-center gap-2 transition-colors hover:text-blue-400"
                        style={{ color: `${localTheme.textColor}99` }}
                      >
                        <Share2 size={18} />
                      </button>
                    </div>

                    {/* Comment Preview - Max 3 comments */}
                    {(post as any).replies && (post as any).replies.length > 0 && (() => {
                      const previewComments = selectPreviewComments((post as any).replies);
                      return (
                        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                          <div className="space-y-3">
                            {previewComments.map((reply) => (
                              <div key={reply.id} className="flex gap-3">
                                <Link
                                  to={`/user/${reply.author.username}`}
                                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                  }}
                                >
                                  {reply.author.profilePicture ? (
                                    <img
                                      src={reply.author.profilePicture}
                                      alt={reply.author.username}
                                      className="w-full h-full rounded-full object-cover"
                                    />
                                  ) : (
                                    <PixelatedTurtle key={`turtle-comment-${localTheme.turtleColor}`} size={16} color={localTheme.turtleColor} />
                                  )}
                                </Link>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Link
                                      to={`/user/${reply.author.username}`}
                                      className="text-xs font-semibold hover:opacity-80"
                                      style={{ color: localTheme.textColor }}
                                    >
                                      {reply.author.profileName || reply.author.username}
                                    </Link>
                                    <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>
                                      {new Date(reply.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <p className="text-xs" style={{ color: localTheme.textColor }}>{reply.content}</p>
                                  <div className="flex items-center gap-3 mt-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        likeComment(reply.id, post.id);
                                      }}
                                      className="flex items-center gap-1 transition-colors hover:opacity-80"
                                      style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '99' }}
                                    >
                                      <Heart size={12} fill={reply.liked ? '#ef4444' : 'none'} />
                                      <span className="text-xs">{reply.likes}</span>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dislikeComment(reply.id, post.id);
                                      }}
                                      className="flex items-center gap-1 transition-colors hover:opacity-80"
                                      style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '99' }}
                                    >
                                      <ThumbsDown size={12} fill={reply.disliked ? '#f97316' : 'none'} />
                                      <span className="text-xs">{reply.dislikes}</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {post.comments > 3 && (
                            <div className="mt-2">
                              <button
                                onClick={() => navigate(
                                  post.slug && post.communitySlug
                                    ? (post.postType === 'forum'
                                        ? getForumPostUrl(post.communitySlug, post.slug)
                                        : getCommunityPostUrl(post.communitySlug, post.slug))
                                    : `/posts/${post.id}`
                                )}
                                className="text-xs font-semibold hover:opacity-80 transition-opacity"
                                style={{ color: localTheme.accentColor }}
                              >
                                View all {post.comments} comments
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              });
              })()
            )}
          </div>
        )}

            {activeTab === 'replies' && (
              <div className="w-full" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '2rem'
              }}>
                {replies.length === 0 ? (
                  <div className="rounded-xl p-6" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
                    <p className="text-center opacity-60" style={{ color: localTheme.textColor }}>
                      No replies yet
                    </p>
                  </div>
                ) : (
                  replies.map((reply) => {
                    const postUrl = reply.type === 'legacy'
                      ? getLegacyPostUrl(reply.post.author.username, reply.post.slug || String(reply.post.id))
                      : getCommunityPostUrl(reply.post.communitySlug, reply.post.slug || String(reply.post.id));

                    return (
                      <div
                        key={reply.id}
                        className="rounded-xl p-6 transition-all hover:scale-105"
                        style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}
                      >
                        {/* Reply content */}
                        <div className="mb-4">
                          <p className="text-sm opacity-70 mb-2" style={{ color: localTheme.textColor }}>
                            replied to @{reply.post.author.username}
                            {reply.post.title && (
                              <span> — {reply.post.title}</span>
                            )}
                          </p>
                          <p className="mb-4" style={{ color: localTheme.textColor }}>
                            {reply.content}
                          </p>
                          <p className="text-xs opacity-50" style={{ color: localTheme.textColor }}>
                            {new Date(reply.createdAt).toLocaleDateString()} at {new Date(reply.createdAt).toLocaleTimeString()}
                          </p>
                        </div>

                        {/* Original post context */}
                        <div
                          className="rounded-lg p-4 border-l-4 cursor-pointer hover:opacity-80 transition-opacity"
                          style={{
                            background: theme.cardBg.replace(/[\d.]+\)$/, '0.6)'),
                            borderColor: localTheme.accentColor
                          }}
                          onClick={() => navigate(postUrl)}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            {reply.post.author.avatar && (
                              <img
                                src={resolveAvatar(reply.post.author.avatar) || reply.post.author.avatar}
                                alt={reply.post.author.displayName}
                                className="w-8 h-8 rounded-full"
                              />
                            )}
                            <div>
                              <p className="font-semibold text-sm" style={{ color: localTheme.textColor }}>
                                {reply.post.author.displayName}
                              </p>
                              <p className="text-xs opacity-60" style={{ color: localTheme.textColor }}>
                                @{reply.post.author.username}
                              </p>
                            </div>
                          </div>
                          {reply.post.title && (
                            <h3 className="font-bold mb-2" style={{ color: localTheme.textColor }}>
                              {reply.post.title}
                            </h3>
                          )}
                          <p
                            className="text-sm opacity-80 line-clamp-3"
                            style={{ color: localTheme.textColor }}
                          >
                            {reply.post.content}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Navigation - Mobile Only */}
        {isMobile && (
          <div className="fixed bottom-0 left-0 right-0 p-2" style={{ background: getCardBackground(localTheme, customization, theme.cardBg) }}>
            <div className="max-w-4xl mx-auto flex justify-around">
              {[
                { name: 'Ads', path: '/ads' },
                { name: 'Messages', path: '/messages' },
                { name: 'Home', path: '/feed' },
                { name: 'Market', path: '/marketplace' },
                { name: 'Post', path: '/new-post' }
              ].map((item) => (
                <button
                  key={item.name}
                  onClick={() => navigate(item.path)}
                  className="px-2 py-2 rounded-lg font-semibold transition-all pixelated text-xs"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: localTheme.textColor
                  }}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Share/Post Button - Position based on card alignment */}
      <button
        onClick={() => {
          setIsShareAnimating(true);
          setTimeout(() => {
            setShowPostWidget(true);
            setIsShareAnimating(false);
          }, 400);
        }}
        className="fixed rounded-lg shadow-lg font-semibold overflow-hidden"
        style={{
          backgroundColor: localTheme.accentColor,
          color: localTheme.textColor,
          zIndex: 1000,
          paddingLeft: `${scaleW(28, scale.w)}px`,
          paddingRight: `${scaleW(28, scale.w)}px`,
          paddingTop: `${scaleH(10, scale.h)}px`,
          paddingBottom: `${scaleH(10, scale.h)}px`,
          fontSize: `${scaleW(18, scale.w)}px`,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: showPostWidget ? 'scale(1.05)' : 'scale(1)',
          ...(profileCustomization.cardPosition === 'left'
            ? { right: `${scaleW(24, scale.w)}px`, top: `calc(${scaleH(160, scale.h)}px + 1.5rem)` }
            : { left: `${scaleW(24, scale.w)}px`, top: `calc(${scaleH(96, scale.h)}px + 1.5rem)` })
        }}
      >
        <span style={{
          display: 'inline-block',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isShareAnimating ? 0 : 1,
          transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
        }}>
          {showPostWidget ? 'Post' : 'Share'}
        </span>
      </button>

      {/* Standardized Theme Customizer Button */}
      <ThemeCustomizerButton
        onClick={handleToggleShowCustomizer}
        tooltipText="Customize Profile Theme"
        accentColor={localTheme.accentColor}
        textColor={localTheme.textColor}
        show={user.isCurrentUser}
      />

      {/* Moderation Button - Position based on card alignment (below Customize Profile button) */}
      {(currentUser && ((currentUser as any).isSiteModerator || (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || isAdmin)) && !user.isCurrentUser && (
        <button
          onClick={handleToggleShowModerationModal}
          className="fixed rounded-lg shadow-lg font-semibold overflow-hidden flex items-center gap-2"
          style={{
            backgroundColor: localTheme.accentColor,
            color: localTheme.textColor,
            zIndex: 1000,
            padding: '0.75rem 2rem',
            fontSize: '1.125rem',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: showModerationModal ? 'scale(1.05)' : 'scale(1)',
            ...(profileCustomization.cardPosition === 'left'
              ? { right: '1.5rem', top: '14rem' }
              : { left: '1.5rem', top: '10rem' })
          }}
        >
          <Shield size={20} />
          <span>Moderate</span>
        </button>
      )}

      {renderModals()}

    </div>
  );
}
